import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ClearIcon from "@mui/icons-material/Clear";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import es from "date-fns/locale/es";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { db, storage, auth } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { fetchProjectsAndProducts } from "../services/projectsApi";
import {
  analyzeTranscription,
  analyzeTranscriptionWithAttendance,
  analyzeCsvOnly,
  convertDatesToObjects,
} from "../services/geminiService";
import {
  unifiedAnalyzeTranscription,
  unifiedAnalyzeCsvOnly,
  unifiedAnalyzeTranscriptionWithAttendance,
} from "../services/unifiedAiService";
import { getAiConfig, fetchAiConfigFromDb } from "../services/aiConfigService";
import { hasRole, ROLES } from "../userRoles";
import AiParametersDialog from "./AiParametersDialog";
import SettingsIcon from "@mui/icons-material/Settings";
import mammoth from "mammoth";
import styles from "../styles/actaFormStyles";
import { PDFViewer } from "@react-pdf/renderer";
import ActaPDF from "./ActaPDF";
import ObjetivoRichEditor, { normalizeObjetivo, extractPlainTextFromBlocks } from "./ObjetivoRichEditor";

const InfoIconWithTooltip = ({ y, title }) => (
  <Tooltip title={title} placement="right">
    <IconButton size="small" sx={{ transform: `translateY(${y}%)`, p: 0 }}>
      <InfoIcon sx={{ fontSize: "1rem", color: "action.active" }} />
    </IconButton>
  </Tooltip>
);

const ActaForm = ({ onSubmit, nextActaNumber, editingActa, userRole, onNavigateToAiConfig }) => {
  const isAdmin = hasRole(userRole, ROLES.ADMIN);
  // Inicializar la variable de intento de envío
  window.attemptedSubmit = false;

  // Agregar nuevos estados para controlar la visibilidad
  const [showSelectedProjects, setShowSelectedProjects] = useState(false);
  const [showSelectedProducts, setShowSelectedProducts] = useState(false);
  
  // Estado para controlar la vinculación de contratos/proyectos en actas de tipo COMITE y OTRO
  const [vinculaContratoProyecto, setVinculaContratoProyecto] = useState(false);

  useEffect(() => {
    if (editingActa && (editingActa.tipoActa === "COMITE" || editingActa.tipoActa === "OTRO")) {
      const hasProjects = editingActa.tipoProyectos && 
                         editingActa.tipoProyectos.length > 0 && 
                         !editingActa.tipoProyectos.includes('sin_proyecto');
      setVinculaContratoProyecto(hasProjects);
    }
  }, [editingActa]);

  const [projects, setProjects] = useState([]);
  const [comites, setComites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingComites, setLoadingComites] = useState(false);
  const [categoriasOtro, setCategoriasOtro] = useState([]);
  const [loadingCategoriasOtro, setLoadingCategoriasOtro] = useState(false);
  // Estados para creación manual de comités y categorías otro
  const [nuevoComiteNombre, setNuevoComiteNombre] = useState("");
  const [nuevaCategoriaOtroNombre, setNuevaCategoriaOtroNombre] = useState("");
  const [uploadNuevoComiteNombre, setUploadNuevoComiteNombre] = useState("");
  const [uploadNuevaCategoriaOtroNombre, setUploadNuevaCategoriaOtroNombre] = useState("");
  const [error, setError] = useState(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [formData, setFormData] = useState({
    tipoActa: "",
    tipoActaCustom: "",
    comiteId: "",
    tipoProyectos: [], // Array para almacenar múltiples proyectos
    tipoProyectoCustom: "",
    productosProyecto: [], // Array para almacenar múltiples productos
    numeroActa: nextActaNumber.toString(),
    fecha: new Date(),
    horaInicio: null,
    horaFinal: null,
    lugar: "",
    objetivo: "",
    ordenDia: [],
    compromisos: [],
    asistentes: [],
    responsable: "",
    imagenesAdjuntas: [], // Array de objetos {nombre: '', imagen: '', id: ''}
    estadoActa: "en_curso", // Estado del acta (sin iniciar, en curso, finalizado) - por defecto en curso
    approvalStatus: "por aprobar", // Inicializar el estado de aprobación
    isDraft: false, // Indicar si es un borrador
    encargadosAprobacion: [], // Array para almacenar encargados por combinación contrato/proyecto
    sinFirma: false, // Nuevo campo para indicar si el acta no requiere firma
  });

  // Estado para manejar la selección de productos
  const [productoSeleccionado, setProductoSeleccionado] = useState("");

  // Estado para manejar la selección de proyectos
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState("");

  // Estados para controlar la edición de elementos
  const [editingOrdenIndex, setEditingOrdenIndex] = useState(null);
  const [editingCompromisoIndex, setEditingCompromisoIndex] = useState(null);
  const [editingAsistenteIndex, setEditingAsistenteIndex] = useState(null);

  // Estados para almacenar los valores temporales durante la edición
  const [tempOrdenItem, setTempOrdenItem] = useState({
    titulo: "",
    descripcion: "",
  });
  const [tempCompromiso, setTempCompromiso] = useState({
    compromiso: "",
    responsable: "",
    fechaCumplimiento: null,
  });
  const [tempAsistente, setTempAsistente] = useState({
    nombre: "",
    cargo: "",
    institucion: "",
    asistio: "Presencial",
    email: "",
    firma: "",
  });

  // Estados para edición del número de acta
  const [editingNumeroActa, setEditingNumeroActa] = useState(false);
  const [tempNumeroActa, setTempNumeroActa] = useState("");
  const [numeroActaError, setNumeroActaError] = useState("");

  // Estados para el diálogo del responsable del acta

  // Generar un ID único para un conjunto de proyectos
  const generarIdProyectos = (proyectos) => {
    // Ordenar los proyectos para asegurar que el mismo conjunto siempre genere el mismo ID
    const proyectosOrdenados = [...proyectos].sort();
    return proyectosOrdenados.join("_");
  };

  // Función para generar combinaciones contrato/proyecto
  const generarCombinacionesContratoProyecto = () => {
    const combinaciones = [];

    if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
      formData.tipoProyectos.forEach((contrato) => {
        if (
          formData.productosProyecto &&
          formData.productosProyecto.length > 0
        ) {
          // Si hay productos, crear combinación contrato/proyecto
          formData.productosProyecto.forEach((proyecto) => {
            const contratoNombre =
              projects.find((p) => p.id === contrato)?.name || contrato;
            combinaciones.push({
              contrato: contrato,
              contratoNombre: contratoNombre,
              proyecto: proyecto,
              key: `${contrato}_${proyecto}`,
              displayName: `${contratoNombre} / ${proyecto}`,
            });
          });
        } else {
          // Si no hay productos, solo contrato
          const contratoNombre =
            projects.find((p) => p.id === contrato)?.name || contrato;
          combinaciones.push({
            contrato: contrato,
            contratoNombre: contratoNombre,
            proyecto: null,
            key: contrato,
            displayName: contratoNombre,
          });
        }
      });
    }

    return combinaciones;
  };

  // Función para actualizar encargados cuando cambian los contratos/proyectos - ELIMINADA
  // Los encargados de aprobación se asignan ahora al momento de aprobar el acta

  // Funciones de encargados eliminadas - se asignan al momento de aprobar

  // Función para eliminar archivo de transcripción
  const handleRemoveTranscriptionFile = () => {
    setTranscriptionFile(null);
    // Verificar si aún hay archivos para procesar
    setReadyToProcess(csvFile !== null);
  };

  // Función para eliminar archivo CSV
  const handleRemoveCsvFile = () => {
    setCsvFile(null);
    // Verificar si aún hay archivos para procesar
    setReadyToProcess(transcriptionFile !== null);
  };

  // Función para validar si los contratos seleccionados ya tienen historial de actas
  const validateContractHistory = async (proyectos, productos) => {
    try {
      if (!proyectos || proyectos.length === 0) {
        return { hasHistory: false, message: "" };
      }

      const actasRef = collection(db, "actas");

      // Solo usar un filtro array-contains-any para proyectos
      const q = query(
        actasRef,
        where("tipoProyectos", "array-contains-any", proyectos)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
        const proyectosSeleccionados = [...proyectos].sort();
        const productosSeleccionados = productos ? [...productos].sort() : [];

        const actasExactas = querySnapshot.docs.filter((doc) => {
          const data = doc.data();

          // Excluir actas en borrador
          if (data.approvalStatus === "borrador" || data.isDraft === true) {
            return false;
          }

          const actaProyectos = data.tipoProyectos
            ? [...data.tipoProyectos].sort()
            : [];
          const actaProductos = data.productosProyecto
            ? [...data.productosProyecto].sort()
            : [];

          // Verificar si es exactamente la misma combinación de proyectos y productos
          const mismaCombinaciónProyectos =
            JSON.stringify(actaProyectos) ===
            JSON.stringify(proyectosSeleccionados);
          const mismaCombinaciónProductos =
            JSON.stringify(actaProductos) ===
            JSON.stringify(productosSeleccionados);

          return mismaCombinaciónProyectos && mismaCombinaciónProductos;
        });

        if (actasExactas.length > 0) {
          const actasCount = actasExactas.length;
          const proyectosStr = proyectos
            .map((p) => {
              const proyecto = projects.find((proj) => proj.id === p);
              return proyecto ? proyecto.name : p;
            })
            .join(", ");

          let message = `Los contratos/proyectos seleccionados (${proyectosStr}) ya tienen ${actasCount} acta(s) registrada(s).`;

          if (productos && productos.length > 0) {
            const productosStr = productos.join(", ");
            message += ` Para los productos: ${productosStr}.`;
          }

          return { hasHistory: true, message, count: actasCount };
        }
      }

      return { hasHistory: false, message: "" };
    } catch (error) {
      console.error("Error al validar historial de contratos:", error);
      return {
        hasHistory: false,
        message: "Error al validar historial de contratos.",
      };
    }
  };

  // Estado para indicar si estamos en modo edición
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Estado para controlar si ya se mostró el alert de edición
  const [editAlertShown, setEditAlertShown] = useState(false);

  // Estados para la funcionalidad de IA
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiSuccess, setAiSuccess] = useState("");
  const [showAiAlert, setShowAiAlert] = useState(false);
  const [aiParametersOpen, setAiParametersOpen] = useState(false);
  const [aiMetricsInfo, setAiMetricsInfo] = useState(null);
  const [currentAiConfig, setCurrentAiConfig] = useState(getAiConfig());

  // Sincronizar configuración global de IA desde Firestore al montar
  useEffect(() => {
    let isMounted = true;
    fetchAiConfigFromDb()
      .then((cfg) => {
        if (isMounted && cfg) {
          setCurrentAiConfig(cfg);
        }
      })
      .catch((err) => console.warn("No se pudo cargar config de IA:", err));
    return () => {
      isMounted = false;
    };
  }, []);

  // Estados para manejo de archivos CSV de asistencia
  const [csvFile, setCsvFile] = useState(null);
  const [transcriptionFile, setTranscriptionFile] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [readyToProcess, setReadyToProcess] = useState(false);

  // Estado para el modal de previsualización del PDF
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Estado para almacenar el proyecto seleccionado
  const [selectedProject, setSelectedProject] = useState(null);

  // Estado para almacenar la información del responsable del proyecto
  const [projectManager, setProjectManager] = useState("");

  // Estados de encargados eliminados - se asignan al momento de aprobar

  // Estado para almacenar los compromisos pendientes de actas anteriores
  const [previousCommitments, setPreviousCommitments] = useState([]);
  const [loadingCommitments, setLoadingCommitments] = useState(false);

  // Estado para almacenar los puntos del orden del día de actas anteriores
  const [previousOrdenDia, setPreviousOrdenDia] = useState([]);
  const [loadingPreviousOrdenDia, setLoadingPreviousOrdenDia] = useState(false);

  // Estados para el diálogo de subir acta definitiva
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    tipoActa: "REUNION",
    tipoActaCustom: "",
    comiteId: "",
    proyecto: "",
    producto: "",
    numeroActa: "",
    pdfFile: null,
    fecha: new Date(),
    objetivo: "",
    responsable: "",
    lugar: "",
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadActaNumberError, setUploadActaNumberError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [nuevoCompromiso, setNuevoCompromiso] = useState({
    compromiso: "",
    responsable: "",
    fechaCumplimiento: null,
  });

  const [nuevoOrdenItem, setNuevoOrdenItem] = useState({
    titulo: "",
    descripcion: "",
  });

  const [nuevoAsistente, setNuevoAsistente] = useState({
    nombre: "",
    cargo: "",
    institucion: "",
    asistio: "Presencial",
    email: "",
    firma: "", // Campo para almacenar la firma en base64
  });

  // Estados para el sistema de caché local
  const [showCacheDialog, setShowCacheDialog] = useState(false);
  const [cachedData, setCachedData] = useState(null);
  const CACHE_KEY = "acta_form_cache";

  // Funciones para manejar el caché local
  const saveToCache = (data) => {
    try {
      // Solo guardar si no estamos editando un acta existente
      if (!editingActa) {
        const cacheData = {
          ...data,
          timestamp: Date.now(),
          // Convertir fechas a strings para el localStorage
          fecha: data.fecha ? data.fecha.toISOString() : null,
          horaInicio: data.horaInicio ? data.horaInicio.toISOString() : null,
          horaFinal: data.horaFinal ? data.horaFinal.toISOString() : null,
          compromisos: data.compromisos.map((c) => ({
            ...c,
            fechaCumplimiento: c.fechaCumplimiento
              ? c.fechaCumplimiento.toISOString()
              : null,
          })),
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      }
    } catch (error) {
      console.error("Error al guardar en caché:", error);
    }
  };

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        // Convertir strings de fechas de vuelta a objetos Date
        return {
          ...data,
          fecha: data.fecha ? new Date(data.fecha) : new Date(),
          horaInicio: data.horaInicio ? new Date(data.horaInicio) : null,
          horaFinal: data.horaFinal ? new Date(data.horaFinal) : null,
          compromisos: data.compromisos.map((c) => ({
            ...c,
            fechaCumplimiento: c.fechaCumplimiento
              ? new Date(c.fechaCumplimiento)
              : null,
          })),
        };
      }
    } catch (error) {
      console.error("Error al cargar desde caché:", error);
    }
    return null;
  };

  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error("Error al limpiar caché:", error);
    }
  };

  const hasSignificantData = (data) => {
    if (!data) return false;

    // Verificar si hay datos significativos (más que solo valores por defecto)
    return (
      (data.tipoActa && data.tipoActa !== "") ||
      (data.tipoActaCustom && data.tipoActaCustom !== "") ||
      (data.comiteId && data.comiteId !== "") ||
      (data.tipoProyectos && data.tipoProyectos.length > 0) ||
      (data.productosProyecto && data.productosProyecto.length > 0) ||
      (data.lugar && data.lugar !== "") ||
      (typeof data.objetivo === 'object' ? Boolean(data.objetivo?.textFallback || data.objetivo?.blocks?.[0]?.content) : (data.objetivo && data.objetivo !== "")) ||
      (data.responsable && data.responsable !== "") ||
      (data.ordenDia && data.ordenDia.length > 0) ||
      (data.compromisos && data.compromisos.length > 0) ||
      (data.asistentes && data.asistentes.length > 0) ||
      (data.imagenesAdjuntas && data.imagenesAdjuntas.length > 0)
    );
  };

  const handleContinueWithCache = () => {
    if (cachedData) {
      setFormData(cachedData);
    }
    setShowCacheDialog(false);
    setCachedData(null);
  };

  const handleStartNew = () => {
    clearCache();
    setShowCacheDialog(false);
    setCachedData(null);
    // El formulario ya tiene los valores por defecto
  };

  // Calcular progreso del formulario
  useEffect(() => {
    let fieldsCompleted = 0;
    let totalFields = 8; // campos básicos

    if (formData.tipoActa) fieldsCompleted++;
    if (formData.tipoProyecto) fieldsCompleted++;
    if (formData.responsable) fieldsCompleted++;
    if (formData.lugar) fieldsCompleted++;
    if (formData.fecha) fieldsCompleted++;
    if (formData.horaInicio) fieldsCompleted++;
    if (formData.horaFinal) fieldsCompleted++;
    const hasObj = typeof formData.objetivo === 'object'
      ? Boolean(formData.objetivo?.textFallback || formData.objetivo?.blocks?.[0]?.content)
      : Boolean(formData.objetivo);
    if (hasObj) fieldsCompleted++;

    // Añadir puntos de orden del día (1 punto = 5%)
    if (formData.ordenDia.length > 0) {
      fieldsCompleted += Math.min(2, formData.ordenDia.length * 0.5);
    }

    // Añadir compromisos (1 compromiso = 5%)
    if (formData.compromisos.length > 0) {
      fieldsCompleted += Math.min(2, formData.compromisos.length * 0.5);
    }

    // Añadir asistentes (1 asistente = 5%)
    if (formData.asistentes.length > 0) {
      fieldsCompleted += Math.min(2, formData.asistentes.length * 0.5);
    }

    const percentage = Math.min(
      100,
      Math.round((fieldsCompleted / (totalFields + 6)) * 100)
    );
    setProgressPercentage(percentage);
  }, [formData]);

  // Verificar caché al cargar el componente
  useEffect(() => {
    // Solo verificar caché si no estamos editando un acta existente
    if (!editingActa) {
      const cached = loadFromCache();
      if (cached && hasSignificantData(cached)) {
        setCachedData(cached);
        setShowCacheDialog(true);
      }
    }
  }, [editingActa]);

  // Guardar en caché cuando cambie formData (con debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!editingActa && hasSignificantData(formData)) {
        saveToCache(formData);
      }
    }, 1000); // Esperar 1 segundo después del último cambio

    return () => clearTimeout(timeoutId);
  }, [formData, editingActa]);

  // Limpiar caché al desmontar el componente si se envió exitosamente
  useEffect(() => {
    return () => {
      // Solo limpiar si se intentó enviar el formulario
      if (window.attemptedSubmit) {
        clearCache();
        window.attemptedSubmit = false;
      }
    };
  }, []);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const projectsData = await fetchProjectsAndProducts();

        // Procesar los datos para convertir el objeto Product en un array products
        const processedProjects = projectsData.map((project) => {
          // Extraer productos del objeto Product (product_0, product_1, etc.)
          const products = [];
          if (project.Product) {
            Object.values(project.Product).forEach((product) => {
              if (product) products.push(product);
            });
          }
          return {
            ...project,
            products, // Añadir array de productos
          };
        });

        setProjects(processedProjects);
        console.log("Proyectos procesados:", processedProjects);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setError(
          error.message ||
            "Directorio de proyectos no disponible. Por favor, intente más tarde."
        );
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Cargar comités desde Firestore
  useEffect(() => {
    const fetchComites = async () => {
      try {
        setLoadingComites(true);
        const comitesRef = collection(db, "comites");
        const q = query(comitesRef, orderBy("nombre"));
        const querySnapshot = await getDocs(q);

        const comitesData = [];
        querySnapshot.forEach((doc) => {
          comitesData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setComites(comitesData);
      } catch (error) {
        console.error("Error al cargar comités:", error);
      } finally {
        setLoadingComites(false);
      }
    };

    // Solo cargar comités si el tipo de acta es COMITE (formulario principal o subida externa)
    if (
      formData.tipoActa === "COMITE" ||
      uploadFormData.tipoActa === "COMITE"
    ) {
      fetchComites();
    }
  }, [formData.tipoActa, uploadFormData.tipoActa]);

  useEffect(() => {
    const fetchCategoriasOtro = async () => {
      try {
        setLoadingCategoriasOtro(true);
        const categoriasRef = collection(db, "categorias_otro");
        const q = query(categoriasRef, orderBy("nombre"));
        const querySnapshot = await getDocs(q);

        const categoriasData = [];
        querySnapshot.forEach((doc) => {
          categoriasData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setCategoriasOtro(categoriasData);
      } catch (error) {
        console.error("Error al cargar categorías de otro:", error);
      } finally {
        setLoadingCategoriasOtro(false);
      }
    };

    if (formData.tipoActa === "OTRO" || uploadFormData.tipoActa === "OTRO") {
      fetchCategoriasOtro();
    }
  }, [formData.tipoActa, uploadFormData.tipoActa]);

  // Update acta number when nextActaNumber changes (solo si no estamos editando)
  useEffect(() => {
    if (!editingActa) {
      // Si es COMITE o OTRO y NO vincula, no actualizar con nextActaNumber global
      if ((formData.tipoActa === 'COMITE' || formData.tipoActa === 'OTRO') && !vinculaContratoProyecto) return;

      setFormData((prev) => ({
        ...prev,
        numeroActa: nextActaNumber.toString(),
      }));
    }
  }, [nextActaNumber, editingActa, formData.tipoActa, vinculaContratoProyecto]);

  // Actualizar número de acta para Comité cuando no hay vinculación
  useEffect(() => {
    if (!editingActa && formData.tipoActa === 'COMITE' && !vinculaContratoProyecto && formData.comiteId) {
      fetchNextActaNumberForCommittee(formData.comiteId);
    } else if (!editingActa && formData.tipoActa === 'OTRO' && !vinculaContratoProyecto && formData.tipoActaCustom) {
      fetchNextActaNumberForCategoryOther(formData.tipoActaCustom);
    }
  }, [formData.comiteId, formData.tipoActaCustom, formData.tipoActa, vinculaContratoProyecto, editingActa]);

  // Efecto para actualizar el número de acta cuando cambian los proyectos
  useEffect(() => {
    if (
      !editingActa &&
      formData.tipoProyectos &&
      formData.tipoProyectos.length > 0
    ) {
      const idProyectos = formData.tipoProyectos
        .map((p) => p.id)
        .sort()
        .join("-");
      console.log(
        "🔄 Proyectos cambiaron, actualizando número de acta:",
        idProyectos
      );
      updateActaNumberForProjects(idProyectos);
    }
  }, [formData.tipoProyectos, formData.productosProyecto, editingActa]);

  // Efecto para cargar el responsable del contrato cuando cambian los proyectos o productos (tanto nueva acta como edición)
  useEffect(() => {
    const loadProjectManager = async () => {
      if (
        formData.tipoProyectos &&
        formData.tipoProyectos.length > 0 &&
        projects.length > 0
      ) {
        try {
          // Obtener nombres de proyectos seleccionados
          const proyectosNombres = formData.tipoProyectos
            .filter((projectId) => projectId !== "OTRO")
            .map((projectId) => {
              const proyecto = projects.find(
                (p) => p.id === projectId || p.name === projectId
              );
              return proyecto ? proyecto.name : projectId;
            })
            .filter((name) => name !== null && name !== "sin_proyecto");

          if (proyectosNombres.length > 0) {
            // Verificar si hay productos seleccionados
            const productos =
              formData.productosProyecto &&
              formData.productosProyecto.length > 0
                ? formData.productosProyecto
                : [];

            let responsableEmail = null;

            if (productos.length > 0) {
              // Si hay productos, usar la función para combinación contrato+proyecto
              console.log(
                "🔍 Buscando responsable para combinación contrato+proyecto:",
                { proyectosNombres, productos }
              );
              const { getContractProjectEmailWithFallback } = await import(
                "../services/projectsApi"
              );
              responsableEmail = await getContractProjectEmailWithFallback(
                proyectosNombres,
                productos
              );
            } else {
              // Si no hay productos, buscar a nivel de contrato
              console.log(
                "ℹ️ Buscando responsable a nivel de contrato:",
                proyectosNombres[0]
              );
              const { getProjectEmailWithFallback } = await import(
                "../services/projectsApi"
              );
              responsableEmail = await getProjectEmailWithFallback(
                proyectosNombres[0]
              );
            }

            // Fallback si la propia acta que se está editando ya tenía customProjectEmail
            if (!responsableEmail && editingActa && editingActa.customProjectEmail) {
              responsableEmail = editingActa.customProjectEmail;
            }

            console.log("📧 Responsable encontrado:", responsableEmail);
            setProjectManager(responsableEmail || "");
          } else {
            setProjectManager("");
          }
        } catch (error) {
          console.error(
            "Error al cargar responsable del proyecto:",
            error
          );
          setProjectManager("");
        }
      } else if (!formData.tipoProyectos || formData.tipoProyectos.length === 0) {
        setProjectManager("");
      }
    };

    loadProjectManager();
  }, [
    formData.tipoProyectos,
    formData.productosProyecto,
    editingActa,
    projects,
  ]);

  // Cargar datos del acta cuando estamos en modo edición
  useEffect(() => {
    if (editingActa) {
      // Mostrar alert recordatorio para el modo edición solo una vez
      if (!editAlertShown) {
        alert('Recordatorio: Está editando un acta existente. Para guardar los cambios, debe ir al final del formulario y hacer clic en el botón "Actualizar acta".');
        setEditAlertShown(true);
      }
      
      // Determinar el tipo de acta
      let tipoActa = editingActa.tipoActa;
      let tipoActaCustom = "";
      let comiteId = "";

      if (tipoActa === "COMITE") {
        comiteId = editingActa.comiteId || "";
      } else if (tipoActa !== "REUNION") {
        tipoActaCustom = tipoActa;
        tipoActa = "OTRO";
      }

      // Determinar el tipo de proyecto
      let tipoProyecto = "";
      let tipoProyectoCustom = "";

      // Buscar el proyecto en la lista de proyectos
      const projectMatch = projects.find(
        (p) => p.name === editingActa.tipoProyecto || p.id === editingActa.tipoProyecto
      );

      if (projectMatch) {
        tipoProyecto = projectMatch.id;
      } else {
        tipoProyecto = "OTRO";
        tipoProyectoCustom = editingActa.tipoProyecto;
      }

      // Convertir productoProyecto a array si es necesario
      const productosProyecto =
        editingActa.productosProyecto ||
        (editingActa.productoProyecto ? [editingActa.productoProyecto] : []);

      // Actualizar el estado con los datos del acta
      setFormData({
        tipoActa,
        tipoActaCustom,
        comiteId,
        tipoProyectos:
          editingActa.tipoProyectos || [tipoProyecto].filter(Boolean), // Asegurar que tipoProyectos sea un array
        tipoProyecto,
        tipoProyectoCustom,
        productosProyecto, // Usar el array de productos
        numeroActa: editingActa.numeroActa,
        fecha: editingActa.fecha,
        horaInicio: editingActa.horaInicio,
        horaFinal: editingActa.horaFinal,
        lugar: editingActa.lugar || "",
        objetivo: editingActa.objetivo || "",
        ordenDia: editingActa.ordenDia || [],
        compromisos: editingActa.compromisos || [],
        asistentes: editingActa.asistentes || [],
        approvalStatus: editingActa.approvalStatus || "",
        isDraft: editingActa.isDraft || false,
        responsable: editingActa.responsable || "",
        sinFirma: editingActa.sinFirma || false,
        imagenesAdjuntas:
          editingActa.imagenesAdjuntas ||
          (editingActa.imagenAdjunta
            ? [
                {
                  id: Date.now(),
                  nombre: "Imagen adjunta",
                  imagen: editingActa.imagenAdjunta,
                },
              ]
            : []),
      });

      // Actualizar el proyecto seleccionado
      if (projectMatch) {
        setSelectedProject(projectMatch);
      }

      // Indicar que estamos en modo edición
      setIsEditMode(true);
    } else {
      // Resetear el estado del alert cuando se salga del modo edición
      setEditAlertShown(false);
    }
  }, [editingActa, projects, editAlertShown]);

  // Función para manejar el cambio de estado del acta
  const handleEstadoActaChange = (e) => {
    const estado = e.target.value;
    setFormData((prev) => ({
      ...prev,
      estadoActa: estado,
    }));

    // Actualizar número de acta basado en el estado.
    if (estado) {
      updateNumeroActaConEstado(estado);
    }
  };

  // Función para actualizar el número de acta con el estado
  const updateNumeroActaConEstado = async (estado) => {
    try {
      console.log("Actualizando número de acta con estado:", estado);
      console.log("Datos del formulario:", formData);

      const actasRef = collection(db, "actas");

      // Construir consulta para obtener TODAS las actas de la combinación ESPECÍFICA de proyectos
      let q;

      // Agregar filtros por la combinación exacta de proyectos si están seleccionados
      if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
        // Solo usar un filtro array-contains-any para proyectos
        q = query(
          actasRef,
          where("tipoProyectos", "array-contains-any", formData.tipoProyectos)
        );
      } else {
        q = query(actasRef);
      }
      const querySnapshot = await getDocs(q);

      let siguienteNumero = 1;
      let nuevoNumeroActa;
      let estadosExistentes = { sin_iniciar: [], en_curso: [], finalizado: [] };

      if (!querySnapshot.empty) {
        // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos
        const proyectosSeleccionados = [...formData.tipoProyectos].sort();
        const productosSeleccionados = formData.productosProyecto
          ? [...formData.productosProyecto].sort()
          : [];

        // Clasificar las actas por estado y extraer números solo de la combinación exacta
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const actaProyectos = data.tipoProyectos
            ? [...data.tipoProyectos].sort()
            : [];
          const actaProductos = data.productosProyecto
            ? [...data.productosProyecto].sort()
            : [];

          // Verificar si es exactamente la misma combinación de proyectos
          const mismaCombinaciónProyectos =
            JSON.stringify(actaProyectos) ===
            JSON.stringify(proyectosSeleccionados);
          const mismaCombinaciónProductos =
            JSON.stringify(actaProductos) ===
            JSON.stringify(productosSeleccionados);

          if (mismaCombinaciónProyectos && mismaCombinaciónProductos) {
            const numeroActa = data.numeroActa;
            const estadoActa = data.estadoActa;

            if (numeroActa && estadoActa) {
              let numero;
              if (numeroActa.startsWith("0.0")) {
                // Formato finalizado: 0.01, 0.02, etc.
                const parteDecimal = numeroActa.substring(3);
                numero = parseInt(parteDecimal);
                if (!isNaN(numero) && numero > 0) {
                  estadosExistentes.finalizado.push(numero);
                }
              } else if (numeroActa.startsWith("0.")) {
                // Formato sin iniciar: 0.1, 0.2, etc.
                const parteDecimal = numeroActa.substring(2);
                numero = parseInt(parteDecimal);
                if (!isNaN(numero) && numero > 0) {
                  estadosExistentes.sin_iniciar.push(numero);
                }
              } else if (!numeroActa.includes(".")) {
                // Formato en curso: 1, 2, 3, etc.
                numero = parseInt(numeroActa);
                if (!isNaN(numero) && numero > 0) {
                  estadosExistentes.en_curso.push(numero);
                }
              }
            }
          }
        });
      }

      // Validaciones de estados secuenciales
      if (estado === "sin_iniciar" && estadosExistentes.en_curso.length > 0) {
        alert(
          'No se puede crear un acta en estado "Sin Iniciar" porque ya existen actas en estado "En Curso" para este proyecto.'
        );
        return;
      }

      if (estado === "en_curso" && estadosExistentes.finalizado.length > 0) {
        alert(
          'No se puede crear un acta en estado "En Curso" porque ya existen actas en estado "Finalizado" para este proyecto.'
        );
        return;
      }

      // Calcular el siguiente número según el estado
      switch (estado) {
        case "sin_iniciar":
          if (estadosExistentes.sin_iniciar.length > 0) {
            siguienteNumero = Math.max(...estadosExistentes.sin_iniciar) + 1;
          }
          nuevoNumeroActa = `0.${siguienteNumero}`;
          break;

        case "en_curso":
          if (estadosExistentes.en_curso.length > 0) {
            siguienteNumero = Math.max(...estadosExistentes.en_curso) + 1;
          }
          nuevoNumeroActa = `${siguienteNumero}`;
          break;

        case "finalizado":
          // Para finalizado, usar el siguiente número basado en el máximo de en_curso
          let maxEnCurso = 0;
          if (estadosExistentes.en_curso.length > 0) {
            maxEnCurso = Math.max(...estadosExistentes.en_curso);
          }

          // El siguiente número para finalizado es el siguiente al máximo en_curso
          siguienteNumero = maxEnCurso + 1;

          // Formatear con dos dígitos: 0.01, 0.02, etc.
          nuevoNumeroActa = `0.0${siguienteNumero}`;
          break;

        default:
          nuevoNumeroActa = `${siguienteNumero}`;
      }

      console.log("Siguiente número:", siguienteNumero);
      console.log("Nuevo número de acta generado:", nuevoNumeroActa);
      console.log(
        "Para la combinación específica de proyectos:",
        formData.tipoProyectos.join(", ")
      );
      if (formData.productosProyecto && formData.productosProyecto.length > 0) {
        console.log("Y productos:", formData.productosProyecto.join(", "));
      }

      setFormData((prev) => ({
        ...prev,
        numeroActa: nuevoNumeroActa,
      }));

      console.log("Número de acta actualizado en el estado");
    } catch (error) {
      console.error("Error al actualizar número de acta con estado:", error);
    }
  };

  // Función para manejar la carga de imágenes
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      // Verificar que sea una imagen
      if (!file.type.startsWith("image/")) {
        alert(`${file.name} no es un archivo de imagen válido.`);
        return;
      }

      // Verificar el tamaño del archivo (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(
          `${file.name} es demasiado grande. Por favor seleccione una imagen menor a 5MB.`
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = String(e.target.result || '');
        
        // Verificar el tamaño del string Base64 (Firebase tiene límite de ~1MB por documento)
        if (base64String.length > 800000) { // ~800KB para dejar margen
          alert(`${file.name} es demasiado grande después de la conversión. Por favor seleccione una imagen más pequeña.`);
          return;
        }
        
        // Crear un objeto simple compatible con Firebase
        const nuevaImagen = {
          id: String(`img_${Date.now()}_${Math.floor(Math.random() * 10000)}`), // Asegurar que sea string
          nombre: String(file.name.split(".")[0] || 'imagen'), // Asegurar que sea string
          imagen: base64String, // String Base64 validado
        };

        setFormData((prev) => ({
          ...prev,
          imagenesAdjuntas: [...prev.imagenesAdjuntas, nuevaImagen],
        }));
      };
      reader.readAsDataURL(file);
    });

    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = "";
  };

  const handleImageNameChange = (id, nuevoNombre) => {
    setFormData((prev) => ({
      ...prev,
      imagenesAdjuntas: prev.imagenesAdjuntas.map((img) =>
        img.id === id ? { ...img, nombre: nuevoNombre } : img
      ),
    }));
  };

  const handleImageDelete = (id) => {
    setFormData((prev) => ({
      ...prev,
      imagenesAdjuntas: prev.imagenesAdjuntas.filter((img) => img.id !== id),
    }));
  };

  // Función para persistir un nuevo comité si fue ingresado manualmente
  const saveNewCommitteeIfNeeded = async (comiteIdVal, manualNombre) => {
    if (comiteIdVal !== "__NUEVO__") {
      const existing = comites.find((c) => c.id === comiteIdVal);
      return { id: comiteIdVal, nombre: existing ? existing.nombre : "" };
    }

    const trimmedNombre = (manualNombre || "").trim();
    if (!trimmedNombre) {
      throw new Error("Debe ingresar el nombre del nuevo comité.");
    }

    // Verificar si ya existe en la lista de comités por nombre
    const existingComite = comites.find(
      (c) => c.nombre && c.nombre.trim().toLowerCase() === trimmedNombre.toLowerCase()
    );
    if (existingComite) {
      return { id: existingComite.id, nombre: existingComite.nombre };
    }

    // Crear en Firestore
    try {
      const newComiteData = {
        nombre: trimmedNombre,
        descripcion: "Creado desde formulario de actas",
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const docRef = await addDoc(collection(db, "comites"), newComiteData);
      const createdObj = { id: docRef.id, ...newComiteData };

      // Actualizar estado local para que esté inmediatamente disponible
      setComites((prev) =>
        [...prev, createdObj].sort((a, b) =>
          (a.nombre || "").localeCompare(b.nombre || "")
        )
      );
      return { id: docRef.id, nombre: trimmedNombre };
    } catch (err) {
      console.error("Error al guardar nuevo comité en Firestore:", err);
      throw new Error("No se pudo guardar el nuevo comité: " + err.message);
    }
  };

  // Función para persistir una nueva categoría OTRO si fue ingresada manualmente
  const saveNewCategoryOtherIfNeeded = async (categoriaVal, manualNombre) => {
    if (categoriaVal !== "__NUEVO__") {
      return { id: categoriaVal, nombre: categoriaVal };
    }

    const trimmedNombre = (manualNombre || "").trim();
    if (!trimmedNombre) {
      throw new Error("Debe ingresar el nombre de la nueva categoría.");
    }

    // Verificar si ya existe en la lista de categorías por nombre
    const existingCat = categoriasOtro.find(
      (c) => c.nombre && c.nombre.trim().toLowerCase() === trimmedNombre.toLowerCase()
    );
    if (existingCat) {
      return { id: existingCat.id, nombre: existingCat.nombre };
    }

    // Crear en Firestore
    try {
      const newCatData = {
        nombre: trimmedNombre,
        descripcion: "Creada desde formulario de actas",
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const docRef = await addDoc(collection(db, "categorias_otro"), newCatData);
      const createdObj = { id: docRef.id, ...newCatData };

      // Actualizar estado local
      setCategoriasOtro((prev) =>
        [...prev, createdObj].sort((a, b) =>
          (a.nombre || "").localeCompare(b.nombre || "")
        )
      );
      return { id: docRef.id, nombre: trimmedNombre };
    } catch (err) {
      console.error("Error al guardar nueva categoría en Firestore:", err);
      throw new Error("No se pudo guardar la nueva categoría: " + err.message);
    }
  };

  // Función para obtener el siguiente número de acta para un comité
  const fetchNextActaNumberForCommittee = async (comiteId, isUploadForm = false) => {
    if (!comiteId) return;

    // Si es un nuevo comité que se está ingresando manualmente, el número base es 1
    if (comiteId === "__NUEVO__") {
      if (isUploadForm) {
        setUploadFormData((prev) => ({ ...prev, numeroActa: "1" }));
      } else if (!vinculaContratoProyecto && !editingActa) {
        setFormData((prev) => ({ ...prev, numeroActa: "1" }));
      }
      return;
    }
    
    // Obtener nombre del comité para búsqueda de respaldo
    const comiteSeleccionado = comites.find(c => c.id === comiteId);
    const nombreComite = comiteSeleccionado ? comiteSeleccionado.nombre : "";
    
    try {
      console.log('🔍 Buscando consecutivo para comité:', comiteId, nombreComite ? `(${nombreComite})` : '');
      const actasRef = collection(db, "actas");
      
      let docsToProcess = [];
      
      // ESTRATEGIA 1: Buscar por ID de comité
      const qId = query(actasRef, where("comiteId", "==", comiteId));
      
      // ESTRATEGIA 2: Buscar por Tipo de Acta exacto "COMITE - [Nombre]"
      let qName = null;
      if (nombreComite) {
        const nombreCompleto = `COMITE - ${nombreComite}`;
        qName = query(actasRef, where("tipoActa", "==", nombreCompleto));
      }

      // Ejecutar consultas en paralelo
      const promises = [getDocs(qId)];
      if (qName) promises.push(getDocs(qName));
      
      const snapshots = await Promise.all(promises);
      
      // Procesar resultados
      snapshots.forEach(snapshot => {
        snapshot.forEach(doc => {
          // Evitar duplicados
          if (!docsToProcess.some(d => d.id === doc.id)) {
            docsToProcess.push({ id: doc.id, data: doc.data() });
          }
        });
      });
      
      let maxNum = 0;
      let count = 0;
      
      docsToProcess.forEach(({ id, data }) => {
        // Verificar si tiene proyectos vinculados reales
        const proyectos = data.tipoProyectos || [];
        const hasProjects = proyectos.length > 0 && 
                           !proyectos.every(p => 
                             p === 'sin_proyecto' || 
                             p === 'Proyecto sin especificar' || 
                             p === data.tipoProyecto || 
                             p === data.tipoActa ||
                             p === nombreComite ||
                             p === data.tipoActaCustom
                           );
        
        // Verificar si es tipo COMITE
        // Aceptamos:
        // 1. tipoActa es "COMITE"
        // 2. tipoActa empieza con "COMITE" (ej: "COMITE - Convivencia")
        const isComiteData = data.tipoActa === 'COMITE' || 
                            (typeof data.tipoActa === 'string' && data.tipoActa.startsWith('COMITE'));
        
        // Verificar que NO sea borrador
        const isNotDraft = data.approvalStatus !== 'borrador' && !data.isDraft;

        if (isComiteData && isNotDraft && !hasProjects) {
          const num = parseInt(data.numeroActa, 10);
          console.log(`📄 Acta encontrada válida: ${id}, Num: ${num}, Status: ${data.approvalStatus}`);
          if (!isNaN(num) && num > maxNum) maxNum = num;
          count++;
        } else {
           // Log para entender por qué se descarta un acta
           console.log(`⚠️ Acta descartada ${id}: isComite=${isComiteData}, isNotDraft=${isNotDraft}, hasProjects=${hasProjects} (Proyectos: ${JSON.stringify(proyectos)}), Status=${data.approvalStatus}`);
        }
      });

      console.log(`✅ Total actas válidas comité: ${count}, Max Num: ${maxNum}, Siguiente: ${maxNum + 1}`);
      
      const nextNum = (maxNum + 1).toString();
      console.log(`📝 Estableciendo número de acta a: ${nextNum}`);
      
      if (isUploadForm) {
        setUploadFormData(prev => ({
          ...prev,
          numeroActa: nextNum
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          numeroActa: nextNum
        }));
      }
    } catch (error) {
      console.error("Error fetching committee acta number:", error);
    }
  };

  // Función para obtener el siguiente número de acta para una categoría OTRO
  const fetchNextActaNumberForCategoryOther = async (categoriaNombre, isUploadForm = false) => {
    if (!categoriaNombre) return;

    // Si es una nueva categoría ingresada manualmente, el consecutivo base es 1
    if (categoriaNombre === "__NUEVO__") {
      if (isUploadForm) {
        setUploadFormData((prev) => ({ ...prev, numeroActa: "1" }));
      } else if (!vinculaContratoProyecto && !editingActa) {
        setFormData((prev) => ({ ...prev, numeroActa: "1" }));
      }
      return;
    }

    try {
      console.log('🔍 Buscando consecutivo para OTRO:', categoriaNombre);
      const actasRef = collection(db, "actas");
      
      // ESTRATEGIA 1: Buscar por tipoActaCustom (nombre de la categoría)
      const q1 = query(actasRef, where("tipoActaCustom", "==", categoriaNombre));
      
      // ESTRATEGIA 2: Buscar por tipoActa (nombre de la categoría) - En algunos casos se guarda directamente aquí
      const q2 = query(actasRef, where("tipoActa", "==", categoriaNombre));
      
      // Ejecutar consultas en paralelo
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(q1),
        getDocs(q2)
      ]);
      
      let docsToProcess = [];
      
      // Recopilar documentos
      snapshot1.forEach(doc => docsToProcess.push({ id: doc.id, data: doc.data() }));
      snapshot2.forEach(doc => {
        if (!docsToProcess.some(d => d.id === doc.id)) {
          docsToProcess.push({ id: doc.id, data: doc.data() });
        }
      });
      
      let maxNum = 0;
      let count = 0;
      
      docsToProcess.forEach(({ id, data }) => {
        // Verificar si tiene proyectos vinculados reales
        const proyectos = data.tipoProyectos || [];
        const hasProjects = proyectos.length > 0 && 
                           !proyectos.every(p => 
                             p === 'sin_proyecto' || 
                             p === 'Proyecto sin especificar' || 
                             p === data.tipoProyecto || 
                             p === data.tipoActa ||
                             p === categoriaNombre ||
                             p === data.tipoActaCustom
                           );
                           
        // Verificar si es tipo OTRO
        // Aceptamos:
        // 1. tipoActa es "OTRO"
        // 2. tipoActa es igual al nombre de la categoría (ej: "2 car")
        // 3. tipoActaCustom es igual al nombre de la categoría
        const isOtroData = data.tipoActa === 'OTRO' || 
                          data.tipoActa === categoriaNombre || 
                          data.tipoActaCustom === categoriaNombre;
        
        // Verificar que NO sea borrador
        const isNotDraft = data.approvalStatus !== 'borrador' && !data.isDraft;
        
        if (isOtroData && isNotDraft && !hasProjects) {
          const num = parseInt(data.numeroActa, 10);
          console.log(`📄 Acta OTRO encontrada válida: ${id}, Num: ${num}, Status: ${data.approvalStatus}`);
          if (!isNaN(num) && num > maxNum) maxNum = num;
          count++;
        } else {
           // Log para entender por qué se descarta un acta
           console.log(`⚠️ Acta OTRO descartada ${id}: isOtro=${isOtroData}, isNotDraft=${isNotDraft}, hasProjects=${hasProjects} (Proyectos: ${JSON.stringify(proyectos)}), Status=${data.approvalStatus}, TipoActa=${data.tipoActa}, TipoCustom=${data.tipoActaCustom}`);
        }
      });
      
      console.log(`✅ Total actas válidas OTRO (${categoriaNombre}): ${count}, Max Num: ${maxNum}, Siguiente: ${maxNum + 1}`);
      
      const nextNum = (maxNum + 1).toString();
      console.log(`📝 Estableciendo número de acta OTRO a: ${nextNum}`);
      
      if (isUploadForm) {
        setUploadFormData(prev => ({
          ...prev,
          numeroActa: nextNum
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          numeroActa: nextNum
        }));
      }
    } catch (error) {
      console.error("Error fetching category other acta number:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Si cambia el proyecto, actualizar el proyecto seleccionado y el número de acta
    if (name === "tipoProyecto") {
      // Resetear el mensaje de error de índice faltante
      window.missingIndexUrl = null;

      if (value === "OTRO") {
        setSelectedProject(null);
        // Actualizar número de acta para proyecto personalizado
        if (formData.estadoActa) {
          updateNumeroActaConEstado(formData.estadoActa);
        }
        // Limpiar compromisos anteriores
        setPreviousCommitments([]);
      } else {
        const project = projects.find((p) => p.id === value);
        setSelectedProject(project);
        // Resetear los productos si se cambia de proyecto
        setFormData((prev) => ({
          ...prev,
          productosProyecto: [],
        }));
        // Actualizar número de acta para el proyecto seleccionado
        if (formData.estadoActa) {
          updateNumeroActaConEstado(formData.estadoActa);
        }
        // Buscar compromisos de actas anteriores para este proyecto
        if (project && project.name) {
          console.log(`Buscando compromisos para el proyecto: ${project.name}`);
          fetchPreviousCommitments(project.name, "");
        } else {
          console.log("No se pudo determinar el nombre del proyecto");
          setPreviousCommitments([]);
        }
      }
    }

    // Mantener compatibilidad con el campo productoProyecto para código existente
    if (name === "productoProyecto") {
      // Resetear el mensaje de error de índice faltante
      window.missingIndexUrl = null;

      if (value) {
        console.log(`Producto seleccionado: ${value}`);

        // Actualizar el array de productos
        setFormData((prev) => ({
          ...prev,
          productosProyecto: [value],
        }));

        updateActaNumberForProduct(value);

        // Buscar compromisos de actas anteriores para este producto
        if (selectedProject && selectedProject.name) {
          const projectName = selectedProject.name;
          console.log(
            `Buscando compromisos para: Proyecto=${projectName}, Producto=${value}`
          );

          // Asegurarse de que se pasa el valor correcto del producto
          // Importante: Usar setTimeout para asegurar que la búsqueda se ejecute después de que el estado se actualice
          setTimeout(() => {
            fetchPreviousCommitments(projectName, value);
          }, 100);
        } else {
          console.log(
            "No se pudo determinar el nombre del proyecto para el producto seleccionado"
          );
          setPreviousCommitments([]);
          setPreviousOrdenDia([]);
        }
      } else {
        console.log("Producto deseleccionado");

        // Si se deselecciona el producto, limpiar el array de productos
        setFormData((prev) => ({
          ...prev,
          productosProyecto: [],
        }));

        // Mostrar solo compromisos del proyecto
        if (selectedProject && selectedProject.name) {
          const projectName = selectedProject.name;
          console.log(
            `Buscando compromisos solo para el proyecto: ${projectName}`
          );
          fetchPreviousCommitments(projectName, "");
        } else {
          console.log("No se pudo determinar el nombre del proyecto");
          setPreviousCommitments([]);
          setPreviousOrdenDia([]);
        }
      }
    }
  };

  // Función para actualizar el número de acta según los proyectos seleccionados
  const updateActaNumberForProjects = async (projectsId) => {
    try {
      // Si hay un estado de acta seleccionado, usar la numeración por estado
      if (formData.estadoActa) {
        updateNumeroActaConEstado(formData.estadoActa);
        return;
      }

      // Si no hay proyectos seleccionados, usar número 1
      if (!formData.tipoProyectos || formData.tipoProyectos.length === 0) {
        setFormData((prev) => ({
          ...prev,
          numeroActa: "1",
        }));
        return;
      }

      console.log(
        "🔄 Calculando número de acta para proyectos:",
        formData.tipoProyectos
      );

      const actasRef = collection(db, "actas");
      let queryConstraints = [];

      // Solo usar un filtro array-contains-any para proyectos
      queryConstraints.push(
        where("tipoProyectos", "array-contains-any", formData.tipoProyectos)
      );

      // Nota: No podemos usar múltiples array-contains-any en Firebase
      // El filtrado por productos se hará en memoria después de obtener los resultados

      const q = query(actasRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      let maxNumeroActa = 0;

      if (!querySnapshot.empty) {
        // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
        const proyectosSeleccionados = [...formData.tipoProyectos].sort();
        const productosSeleccionados = formData.productosProyecto
          ? [...formData.productosProyecto].sort()
          : [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // Excluir actas en borrador
          if (data.approvalStatus === "borrador" || data.isDraft === true) {
            return;
          }

          const actaProyectos = data.tipoProyectos
            ? [...data.tipoProyectos].sort()
            : [];
          const actaProductos = data.productosProyecto
            ? [...data.productosProyecto].sort()
            : [];

          // Verificar si es exactamente la misma combinación de proyectos y productos
          const mismaCombinaciónProyectos =
            JSON.stringify(actaProyectos) ===
            JSON.stringify(proyectosSeleccionados);
          const mismaCombinaciónProductos =
            JSON.stringify(actaProductos) ===
            JSON.stringify(productosSeleccionados);

          if (mismaCombinaciónProyectos && mismaCombinaciónProductos) {
            const numeroActa = data.numeroActa;
            if (numeroActa && !numeroActa.includes(".")) {
              // Solo considerar números enteros (actas en curso)
              const numero = parseInt(numeroActa);
              if (!isNaN(numero) && numero > maxNumeroActa) {
                maxNumeroActa = numero;
              }
            }
          }
        });
      }

      const nextNumber = maxNumeroActa + 1;

      console.log(
        `🔢 Número máximo encontrado para esta combinación: ${maxNumeroActa}`
      );
      console.log(`🔢 Siguiente número de acta: ${nextNumber}`);
      console.log(
        `📋 Proyectos seleccionados: ${formData.tipoProyectos.join(", ")}`
      );
      if (formData.productosProyecto && formData.productosProyecto.length > 0) {
        console.log(
          `📦 Productos seleccionados: ${formData.productosProyecto.join(", ")}`
        );
      }

      setFormData((prev) => ({
        ...prev,
        numeroActa: nextNumber.toString(),
      }));
    } catch (error) {
      console.error("Error al actualizar número de acta:", error);
      // En caso de error, usar número 1
      setFormData((prev) => ({
        ...prev,
        numeroActa: "1",
      }));
    }
  };

  // Función para validar si un número de acta ya existe
  const validateActaNumber = async (
    numeroActa,
    projectId,
    productValue = null
  ) => {
    if (!numeroActa || !projectId) return { isValid: true, message: "" };

    try {
      const selectedProject = projects.find((p) => p.id === projectId);
      if (!selectedProject) return { isValid: true, message: "" };

      const actasRef = collection(db, "actas");
      let queryConstraints = [where("numeroActa", "==", numeroActa)];

      // Para múltiples proyectos, usar tipoProyectos array
      if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
        queryConstraints.push(
          where("tipoProyectos", "array-contains-any", formData.tipoProyectos)
        );
      } else {
        // Fallback para compatibilidad con actas antiguas que usan tipoProyecto
        queryConstraints.push(
          where("tipoProyecto", "==", selectedProject.name)
        );
      }

      // Si hay producto, también filtrar por producto
      if (productValue) {
        queryConstraints.push(
          where("productosProyecto", "array-contains", productValue)
        );
      }

      const q = query(actasRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
      const proyectosSeleccionados = formData.tipoProyectos
        ? [...formData.tipoProyectos].sort()
        : [projectId];
      const productosSeleccionados = productValue ? [productValue] : [];

      const existingActas = querySnapshot.docs.filter((doc) => {
        const data = doc.data();

        // Excluir actas en borrador
        if (data.approvalStatus === "borrador" || data.isDraft === true) {
          return false;
        }

        const actaProyectos = data.tipoProyectos
          ? [...data.tipoProyectos].sort()
          : [data.tipoProyecto];
        const actaProductos = data.productosProyecto
          ? [...data.productosProyecto].sort()
          : [];

        // Verificar si es exactamente la misma combinación de proyectos y productos
        const mismaCombinaciónProyectos =
          JSON.stringify(actaProyectos) ===
          JSON.stringify(proyectosSeleccionados);
        const mismaCombinaciónProductos =
          JSON.stringify(actaProductos) ===
          JSON.stringify(productosSeleccionados);

        return mismaCombinaciónProyectos && mismaCombinaciónProductos;
      });

      if (existingActas.length > 0) {
        return {
          isValid: false,
          message: `El número de acta ${numeroActa} ya existe para esta combinación específica de proyectos${
            productValue ? " y productos" : ""
          }`,
        };
      }

      return { isValid: true, message: "" };
    } catch (error) {
      console.error("Error validando número de acta:", error);
      return { isValid: true, message: "" }; // En caso de error, permitir continuar
    }
  };

  // Función para validar restricciones de estado del acta
  const validateActaStateRestrictions = async (
    projectId,
    productValue = null,
    estadoActa,
    numeroActa
  ) => {
    if (!projectId || !estadoActa) return { isValid: true, message: "" };

    try {
      const selectedProject = projects.find((p) => p.id === projectId);
      if (!selectedProject) return { isValid: true, message: "" };

      const actasRef = collection(db, "actas");
      let queryConstraints = [];

      // Solo usar un filtro para proyectos
      if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
        queryConstraints.push(
          where("tipoProyectos", "array-contains-any", formData.tipoProyectos)
        );
      } else {
        queryConstraints.push(
          where("tipoProyectos", "array-contains", projectId)
        );
      }

      // Nota: El filtrado por productos se hará en memoria para evitar múltiples array-contains

      const q = query(actasRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
      const proyectosSeleccionados = formData.tipoProyectos
        ? [...formData.tipoProyectos].sort()
        : [projectId];
      const productosSeleccionados = productValue ? [productValue] : [];

      const existingActas = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Excluir actas en borrador
        if (data.approvalStatus === "borrador" || data.isDraft === true) {
          return;
        }

        const actaProyectos = data.tipoProyectos
          ? [...data.tipoProyectos].sort()
          : [data.tipoProyecto];
        const actaProductos = data.productosProyecto
          ? [...data.productosProyecto].sort()
          : [];

        // Verificar si es exactamente la misma combinación de proyectos y productos
        const mismaCombinaciónProyectos =
          JSON.stringify(actaProyectos) ===
          JSON.stringify(proyectosSeleccionados);
        const mismaCombinaciónProductos =
          JSON.stringify(actaProductos) ===
          JSON.stringify(productosSeleccionados);

        if (mismaCombinaciónProyectos && mismaCombinaciónProductos) {
          existingActas.push({
            numeroActa: data.numeroActa,
            estadoActa: data.estadoActa || "en_curso",
          });
        }
      });

      // Validaciones según el estado del acta
      if (estadoActa === "sin_iniciar") {
        // Sin iniciar debe empezar en 0
        if (numeroActa !== "0") {
          return {
            isValid: false,
            message: 'Las actas en estado "Sin iniciar" deben tener número 0',
          };
        }

        // No puede haber actas "sin iniciar" si hay actas "en curso"
        const actasEnCurso = existingActas.filter(
          (acta) => acta.estadoActa === "en_curso"
        );
        if (actasEnCurso.length > 0) {
          return {
            isValid: false,
            message:
              'No se puede crear un acta "Sin iniciar" cuando ya existen actas "En curso" para este proyecto',
          };
        }
      }

      if (estadoActa === "finalizado") {
        // Finalizado debe terminar en .0
        if (!numeroActa.includes(".") || !numeroActa.endsWith(".0")) {
          return {
            isValid: false,
            message:
              'Las actas en estado "Finalizado" deben tener número con formato decimal terminado en .0 (ej: 1.0, 2.0)',
          };
        }
      }

      return { isValid: true, message: "" };
    } catch (error) {
      console.error("Error validando restricciones de estado:", error);
      return { isValid: true, message: "" }; // En caso de error, permitir continuar
    }
  };

  // Función para actualizar el número de acta en el formulario de subida externa
  const updateUploadActaNumber = async (projectId, productValue = null) => {
    if (!window.actaNumbering || !projectId) {
      console.warn(
        "⚠️ window.actaNumbering no está disponible o no hay proyecto seleccionado"
      );
      return;
    }

    try {
      // Obtener el proyecto seleccionado
      const selectedProject = projects.find((p) => p.id === projectId);
      if (!selectedProject) {
        console.warn("⚠️ Proyecto no encontrado");
        return;
      }

      const projectKey = selectedProject.name;
      let nextNumber = 1;

      if (productValue) {
        // Si hay producto seleccionado, usar numeración por proyecto-producto
        const productKey = `${projectKey}_${productValue}`;
        if (!window.actaNumbering.byMultiProjectProduct) {
          window.actaNumbering.byMultiProjectProduct = {};
        }
        const maxProductActa =
          window.actaNumbering.byMultiProjectProduct[productKey] || 0;
        nextNumber = maxProductActa + 1;
        console.log(
          `🔢 Número de acta para ${projectKey} - ${productValue}: ${nextNumber}`
        );
      } else {
        // Si no hay producto, usar numeración por proyecto
        if (!window.actaNumbering.byProject) {
          window.actaNumbering.byProject = {};
        }
        const maxProjectActa = window.actaNumbering.byProject[projectKey] || 0;
        nextNumber = maxProjectActa + 1;
        console.log(`🔢 Número de acta para ${projectKey}: ${nextNumber}`);
      }

      // Actualizar el número de acta en uploadFormData
      setUploadFormData((prev) => ({
        ...prev,
        numeroActa: nextNumber.toString(),
      }));

      // Validar el número de acta generado automáticamente
      const validation = await validateActaNumber(
        nextNumber.toString(),
        projectId,
        productValue
      );

      // También validar restricciones de estado si hay estado seleccionado
      let stateValidation = { isValid: true, message: "" };
      if (uploadFormData.estadoActa) {
        stateValidation = await validateActaStateRestrictions(
          projectId,
          productValue,
          uploadFormData.estadoActa,
          nextNumber.toString()
        );
      }

      // Mostrar el primer error encontrado
      if (!validation.isValid) {
        setUploadActaNumberError(validation.message);
      } else if (!stateValidation.isValid) {
        setUploadActaNumberError(stateValidation.message);
      } else {
        setUploadActaNumberError("");
      }
    } catch (error) {
      console.error("Error al actualizar número de acta:", error);
    }
  };

  // Función para actualizar el número de acta con el estado en el formulario de upload
  const updateUploadNumeroActaConEstado = async (
    estado,
    projectId,
    productValue = null
  ) => {
    try {
      console.log("Actualizando número de acta con estado en upload:", estado);
      console.log("Proyecto:", projectId, "Producto:", productValue);

      const actasRef = collection(db, "actas");

      // Construir consulta para obtener TODAS las actas del proyecto (sin filtrar por estado)
      let queryConstraints = [];

      // Agregar filtros por proyecto
      queryConstraints.push(
        where("tipoProyectos", "array-contains", projectId)
      );

      // Si también hay productos seleccionados, filtrar por ellos
      if (productValue) {
        queryConstraints.push(
          where("productosProyecto", "array-contains", productValue)
        );
      }

      const q = query(actasRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      let siguienteNumero = 1;
      let nuevoNumeroActa;
      let estadosExistentes = { sin_iniciar: [], en_curso: [], finalizado: [] };

      if (!querySnapshot.empty) {
        // Clasificar las actas por estado y extraer números
        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // Excluir actas en borrador
          if (data.approvalStatus === "borrador" || data.isDraft === true) {
            return;
          }

          const numeroActa = data.numeroActa;
          const estadoActa = data.estadoActa;

          if (numeroActa && estadoActa) {
            let numero;
            if (numeroActa.startsWith("0.0")) {
              // Formato finalizado: 0.01, 0.02, etc.
              const parteDecimal = numeroActa.substring(3);
              numero = parseInt(parteDecimal);
              if (!isNaN(numero) && numero > 0) {
                estadosExistentes.finalizado.push(numero);
              }
            } else if (numeroActa.startsWith("0.")) {
              // Formato sin iniciar: 0.1, 0.2, etc.
              const parteDecimal = numeroActa.substring(2);
              numero = parseInt(parteDecimal);
              if (!isNaN(numero) && numero > 0) {
                estadosExistentes.sin_iniciar.push(numero);
              }
            } else if (!numeroActa.includes(".")) {
              // Formato en curso: 1, 2, 3, etc.
              numero = parseInt(numeroActa);
              if (!isNaN(numero) && numero > 0) {
                estadosExistentes.en_curso.push(numero);
              }
            }
          }
        });
      }

      // Validaciones de estados secuenciales
      if (estado === "sin_iniciar" && estadosExistentes.en_curso.length > 0) {
        alert(
          'No se puede crear un acta en estado "Sin Iniciar" porque ya existen actas en estado "En Curso" para este proyecto.'
        );
        return;
      }

      if (estado === "en_curso" && estadosExistentes.finalizado.length > 0) {
        alert(
          'No se puede crear un acta en estado "En Curso" porque ya existen actas en estado "Finalizado" para este proyecto.'
        );
        return;
      }

      // Calcular el siguiente número según el estado
      switch (estado) {
        case "sin_iniciar":
          if (estadosExistentes.sin_iniciar.length > 0) {
            siguienteNumero = Math.max(...estadosExistentes.sin_iniciar) + 1;
          }
          nuevoNumeroActa = `0.${siguienteNumero}`;
          break;

        case "en_curso":
          if (estadosExistentes.en_curso.length > 0) {
            siguienteNumero = Math.max(...estadosExistentes.en_curso) + 1;
          }
          nuevoNumeroActa = `${siguienteNumero}`;
          break;

        case "finalizado":
          // Para finalizado, usar el siguiente número basado en el máximo de en_curso
          let maxEnCurso = 0;
          if (estadosExistentes.en_curso.length > 0) {
            maxEnCurso = Math.max(...estadosExistentes.en_curso);
          }

          // El siguiente número para finalizado es el siguiente al máximo en_curso
          siguienteNumero = maxEnCurso + 1;

          // Formatear con dos dígitos: 0.01, 0.02, etc.
          nuevoNumeroActa = `0.0${siguienteNumero}`;
          break;

        default:
          nuevoNumeroActa = `${siguienteNumero}`;
      }

      console.log("Siguiente número:", siguienteNumero);
      console.log("Nuevo número de acta generado:", nuevoNumeroActa);

      setUploadFormData((prev) => ({
        ...prev,
        numeroActa: nuevoNumeroActa,
      }));

      // Limpiar errores
      setUploadActaNumberError("");

      console.log("Número de acta actualizado en el estado de upload");
    } catch (error) {
      console.error(
        "Error al actualizar número de acta con estado en upload:",
        error
      );
    }
  };

  // Función para actualizar el número de acta según el producto seleccionado
  const updateActaNumberForProduct = (productValue) => {
    if (
      !window.actaNumbering ||
      !formData.tipoProyectos ||
      formData.tipoProyectos.length === 0
    )
      return;

    // Si hay un estado de acta seleccionado, usar la numeración por estado
    if (formData.estadoActa) {
      updateNumeroActaConEstado(formData.estadoActa);
      return;
    }

    // Generar ID para el conjunto de proyectos
    const projectsId = generarIdProyectos(formData.tipoProyectos);
    const productKey = `${projectsId}_${productValue}`;

    // Si no existe, crear una nueva entrada
    if (!window.actaNumbering.byMultiProjectProduct) {
      window.actaNumbering.byMultiProjectProduct = {};
    }

    // Obtener el número máximo de acta para este producto con este conjunto de proyectos
    const maxProductActa =
      window.actaNumbering.byMultiProjectProduct[productKey] || 0;
    // Usar el siguiente número disponible para este producto
    const nextNumber = maxProductActa + 1;

    setFormData((prev) => ({
      ...prev,
      numeroActa: nextNumber.toString(),
    }));

    console.log(
      `Número de acta actualizado para producto ${productValue}: ${nextNumber}`
    );
  };

  // Función para actualizar el número de acta según múltiples productos seleccionados
  const updateActaNumberForMultiProducts = async (projectsId, productsId) => {
    // Si hay un estado de acta seleccionado, usar la numeración por estado
    if (formData.estadoActa) {
      updateNumeroActaConEstado(formData.estadoActa);
      return;
    }

    try {
      console.log("🔄 Calculando número de acta para proyectos y productos:", {
        projectsId,
        productsId,
      });

      const actasRef = collection(db, "actas");
      const querySnapshot = await getDocs(actasRef);

      // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
      const proyectosSeleccionados = formData.tipoProyectos
        ? [...formData.tipoProyectos].sort()
        : [];
      const productosSeleccionados = formData.productosProyecto
        ? [...formData.productosProyecto].sort()
        : [];

      const existingActas = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Excluir actas en borrador
        if (data.approvalStatus === "borrador" || data.isDraft === true) {
          return;
        }

        const actaProyectos = data.tipoProyectos
          ? [...data.tipoProyectos].sort()
          : [];
        const actaProductos = data.productosProyecto
          ? [...data.productosProyecto].sort()
          : [];

        // Verificar si es exactamente la misma combinación de proyectos y productos
        const mismaCombinaciónProyectos =
          JSON.stringify(actaProyectos) ===
          JSON.stringify(proyectosSeleccionados);
        const mismaCombinaciónProductos =
          JSON.stringify(actaProductos) ===
          JSON.stringify(productosSeleccionados);

        if (mismaCombinaciónProyectos && mismaCombinaciónProductos) {
          const numeroActa = parseFloat(data.numeroActa);
          if (!isNaN(numeroActa) && !data.numeroActa.includes(".")) {
            existingActas.push(numeroActa);
          }
        }
      });

      // Calcular el siguiente número
      const maxNumber =
        existingActas.length > 0 ? Math.max(...existingActas) : 0;
      const nextNumber = maxNumber + 1;

      console.log(
        `🔢 Siguiente número de acta para combinación específica: ${nextNumber}`
      );

      setFormData((prev) => ({
        ...prev,
        numeroActa: nextNumber.toString(),
      }));
    } catch (error) {
      console.error(
        "Error al actualizar número de acta para múltiples productos:",
        error
      );
      // En caso de error, usar número 1 como fallback
      setFormData((prev) => ({
        ...prev,
        numeroActa: "1",
      }));
    }
  };

  // Función para buscar compromisos de actas anteriores basándose en combinaciones exactas de contratos y proyectos
  const fetchPreviousCommitmentsForContractProjects = async (
    proyectos,
    productos
  ) => {
    if (!proyectos || proyectos.length === 0) {
      console.log("No hay proyectos seleccionados, no se buscarán compromisos");
      setPreviousCommitments([]);
      setPreviousOrdenDia([]);
      return;
    }

    setLoadingCommitments(true);
    setLoadingPreviousOrdenDia(true);
    console.log(
      `Buscando compromisos para combinaciones exactas de contratos y proyectos`
    );
    console.log("Proyectos:", proyectos);
    console.log("Productos:", productos);

    try {
      const actasRef = collection(db, "actas");
      const querySnapshot = await getDocs(actasRef);

      // Obtener los nombres de los proyectos actuales
      const nombresProyectosActuales = proyectos
        .map((proyectoId) => {
          if (proyectoId === "OTRO") return formData.tipoProyectoCustom;
          const proyecto = projects.find((p) => p.id === proyectoId);
          return proyecto ? proyecto.name : proyectoId;
        })
        .filter(Boolean)
        .sort();

      // Obtener los nombres de los productos actuales
      const nombresProductosActuales = productos
        ? productos.filter(Boolean).sort()
        : [];

      console.log("Buscando actas con combinación exacta:");
      console.log("- Proyectos:", nombresProyectosActuales);
      console.log("- Productos:", nombresProductosActuales);

      let actaAnteriorEncontrada = null;
      let fechaMasReciente = null;

      // Buscar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Excluir actas en borrador
        const esBorrador =
          data.approvalStatus === "borrador" || data.isDraft === true;
        if (esBorrador) {
          return;
        }

        // Obtener proyectos del acta (priorizar tipoProyectos sobre tipoProyecto)
        let proyectosActa = [];
        if (
          data.tipoProyectos &&
          Array.isArray(data.tipoProyectos) &&
          data.tipoProyectos.length > 0
        ) {
          proyectosActa = [...data.tipoProyectos].sort();
        } else if (data.tipoProyecto) {
          proyectosActa = [data.tipoProyecto];
        }

        // Obtener productos del acta
        let productosActa = [];
        if (
          data.productosProyecto &&
          Array.isArray(data.productosProyecto) &&
          data.productosProyecto.length > 0
        ) {
          productosActa = [...data.productosProyecto].sort();
        } else if (data.productoProyecto) {
          productosActa = [data.productoProyecto];
        }

        // Verificar si es exactamente la misma combinación
        const mismaCombinaciónProyectos =
          JSON.stringify(proyectosActa) ===
          JSON.stringify(nombresProyectosActuales);
        const mismaCombinaciónProductos =
          JSON.stringify(productosActa) ===
          JSON.stringify(nombresProductosActuales);

        if (mismaCombinaciónProyectos && mismaCombinaciónProductos) {
          // Verificar que tenga al menos un contrato y un proyecto asignado
          const tieneContratos = proyectosActa.length > 0;
          const tieneProyectos = productosActa.length > 0;

          if (tieneContratos && tieneProyectos) {
            const fechaActa = data.createdAt
              ? data.createdAt.toDate
                ? data.createdAt.toDate()
                : new Date(data.createdAt)
              : new Date(0);

            if (!fechaMasReciente || fechaActa > fechaMasReciente) {
              fechaMasReciente = fechaActa;
              actaAnteriorEncontrada = data;
              console.log(
                `Acta anterior encontrada: ${
                  data.numeroActa
                } (${fechaActa.toLocaleDateString()})`
              );
            }
          }
        }
      });

      let compromisosCombinados = [];
      let ordenDiaCombinado = [];

      if (actaAnteriorEncontrada) {
        console.log(
          `Usando acta anterior: ${actaAnteriorEncontrada.numeroActa}`
        );

        // Procesar compromisos del acta anterior
        if (
          actaAnteriorEncontrada.compromisos &&
          actaAnteriorEncontrada.compromisos.length > 0
        ) {
          const commitments = actaAnteriorEncontrada.compromisos.map((c) => {
            let fechaCumplimiento;
            try {
              fechaCumplimiento =
                c.fechaCumplimiento &&
                (c.fechaCumplimiento.toDate
                  ? c.fechaCumplimiento.toDate()
                  : new Date(c.fechaCumplimiento));
            } catch (err) {
              console.error("Error al convertir fecha:", err);
              fechaCumplimiento = new Date();
            }

            return {
              ...c,
              fechaCumplimiento,
              estado: c.estado || "En curso",
              fromPreviousActa: true,
              fromActaNumber: actaAnteriorEncontrada.numeroActa,
            };
          });

          compromisosCombinados = commitments;
        }

        // Procesar orden del día del acta anterior (solo para visualización)
        if (
          actaAnteriorEncontrada.ordenDia &&
          actaAnteriorEncontrada.ordenDia.length > 0
        ) {
          ordenDiaCombinado = actaAnteriorEncontrada.ordenDia.map((item) => ({
            ...item,
            fromPreviousActa: true,
            fromActaNumber: actaAnteriorEncontrada.numeroActa,
          }));
        }
      } else {
        console.log(
          "No se encontró acta anterior con la misma combinación de contratos y proyectos"
        );
      }

      console.log(
        `Se encontraron ${compromisosCombinados.length} compromisos del acta anterior`
      );
      console.log(
        `Se encontraron ${ordenDiaCombinado.length} puntos del orden del día del acta anterior`
      );

      setPreviousCommitments(compromisosCombinados);
      setPreviousOrdenDia(ordenDiaCombinado);
    } catch (error) {
      console.error("Error al buscar compromisos anteriores:", error);
      setPreviousCommitments([]);
      setPreviousOrdenDia([]);
    } finally {
      setLoadingCommitments(false);
      setLoadingPreviousOrdenDia(false);
    }
  };

  // Función para buscar compromisos de actas anteriores para múltiples proyectos (legacy)
  const fetchPreviousCommitmentsForProjects = async (proyectos) => {
    if (!proyectos || proyectos.length === 0) {
      console.log("No hay proyectos seleccionados, no se buscarán compromisos");
      setPreviousCommitments([]);
      return;
    }

    setLoadingCommitments(true);
    console.log(`Buscando compromisos para ${proyectos.length} proyectos`);

    try {
      const actasRef = collection(db, "actas");
      let compromisosCombinados = [];
      let ordenDiaCombinado = [];

      // Obtener los nombres de los proyectos
      const nombresProyectos = proyectos.map((proyectoId) => {
        if (proyectoId === "OTRO") return formData.tipoProyectoCustom;
        const proyecto = projects.find((p) => p.id === proyectoId);
        return proyecto ? proyecto.name : proyectoId;
      });

      // Buscar actas para cada proyecto seleccionado
      for (const nombreProyecto of nombresProyectos) {
        if (!nombreProyecto) continue;

        console.log(`Buscando actas para el proyecto: ${nombreProyecto}`);

        const q = query(
          actasRef,
          where("tipoProyecto", "==", nombreProyecto),
          orderBy("createdAt", "desc")
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Filtrar ESTRICTAMENTE actas que NO tienen productos asociados
          const actasSinProducto = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Excluir actas que están en estado borrador
            const esBorrador =
              data.approvalStatus === "borrador" || data.isDraft === true;
            if (esBorrador) {
              return; // Saltar actas en borrador
            }

            // Verificar que no tenga productos (ni en array ni en campo singular)
            const tieneProductosArray =
              data.productosProyecto && data.productosProyecto.length > 0;
            const tieneProductoSingular =
              data.productoProyecto && data.productoProyecto.trim() !== "";

            // Solo incluir actas que NO tienen productos asociados
            if (!tieneProductosArray && !tieneProductoSingular) {
              actasSinProducto.push(data);
            }
          });

          console.log(
            `Se encontraron ${actasSinProducto.length} actas sin productos para el proyecto ${nombreProyecto}`
          );

          // Si encontramos actas sin producto, usar la más reciente
          if (actasSinProducto.length > 0) {
            // Ordenar por fecha de creación descendente (la más reciente primero)
            actasSinProducto.sort((a, b) => {
              const dateA = a.createdAt
                ? a.createdAt.toDate
                  ? a.createdAt.toDate()
                  : new Date(a.createdAt)
                : new Date(0);
              const dateB = b.createdAt
                ? b.createdAt.toDate
                  ? b.createdAt.toDate()
                  : new Date(b.createdAt)
                : new Date(0);
              return dateB - dateA;
            });
            const latestActa = actasSinProducto[0];
            console.log(
              `Usando acta más reciente sin productos: ${latestActa.numeroActa}`
            );

            // Procesar compromisos
            if (latestActa.compromisos && latestActa.compromisos.length > 0) {
              // Convertir las fechas de Firestore a objetos Date
              const commitments = latestActa.compromisos.map((c) => {
                let fechaCumplimiento;
                try {
                  fechaCumplimiento =
                    c.fechaCumplimiento &&
                    (c.fechaCumplimiento.toDate
                      ? c.fechaCumplimiento.toDate()
                      : new Date(c.fechaCumplimiento));
                } catch (err) {
                  console.error("Error al convertir fecha:", err);
                  fechaCumplimiento = new Date();
                }

                return {
                  ...c,
                  fechaCumplimiento,
                  estado: c.estado || "En curso", // Usar estado existente o establecer 'En curso' por defecto
                  fromPreviousActa: true,
                  fromProject: nombreProyecto, // Agregar el proyecto de origen
                };
              });

              // Agregar los compromisos a la lista combinada
              compromisosCombinados = [
                ...compromisosCombinados,
                ...commitments,
              ];
            }

            // Procesar puntos del orden del día
            if (latestActa.ordenDia && latestActa.ordenDia.length > 0) {
              // Agregar información del acta de origen a cada punto del orden del día
              const ordenDiaConOrigen = latestActa.ordenDia.map((item) => ({
                ...item,
                fromPreviousActa: true,
                fromActaNumber: latestActa.numeroActa,
                fromProject: nombreProyecto,
              }));
              // Agregar los puntos del orden del día a la lista combinada
              ordenDiaCombinado = [...ordenDiaCombinado, ...ordenDiaConOrigen];
            }
          }
        }
      }

      // Eliminar duplicados basados en el texto del compromiso
      const compromisosSinDuplicados = compromisosCombinados.filter(
        (compromiso, index, self) =>
          index ===
          self.findIndex((c) => c.compromiso === compromiso.compromiso)
      );

      // Eliminar duplicados en los puntos del orden del día basados en el título
      const ordenDiaSinDuplicados = ordenDiaCombinado.filter(
        (item, index, self) =>
          index === self.findIndex((i) => i.titulo === item.titulo)
      );

      console.log(
        `Se encontraron ${compromisosSinDuplicados.length} compromisos pendientes de actas anteriores`
      );
      console.log(
        `Se encontraron ${ordenDiaSinDuplicados.length} puntos del orden del día de actas anteriores`
      );

      setPreviousCommitments(compromisosSinDuplicados);
      setPreviousOrdenDia(ordenDiaSinDuplicados);
    } catch (error) {
      console.error("Error al buscar compromisos anteriores:", error);

      // Verificar si es un error de índice de Firebase
      if (
        error.code === "failed-precondition" &&
        error.message &&
        error.message.includes("index")
      ) {
        const indexUrlMatch = error.message.match(
          /https:\/\/console\.firebase\.google\.com[^\s]+/
        );
        const indexUrl = indexUrlMatch ? indexUrlMatch[0] : null;
        window.missingIndexUrl = indexUrl;
        console.log("Se requiere crear un índice en Firebase. URL:", indexUrl);
      }

      setPreviousCommitments([]);
      setPreviousOrdenDia([]);
    } finally {
      setLoadingCommitments(false);
      setLoadingPreviousOrdenDia(false);
    }
  };

  const handleCompromisoChange = (e) => {
    const { name, value } = e.target;
    setNuevoCompromiso((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAsistenteChange = (e) => {
    const { name, value } = e.target;
    setNuevoAsistente((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAgregarCompromiso = () => {
    if (
      nuevoCompromiso.compromiso &&
      nuevoCompromiso.responsable &&
      nuevoCompromiso.fechaCumplimiento
    ) {
      setFormData((prev) => ({
        ...prev,
        compromisos: [...prev.compromisos, nuevoCompromiso],
      }));
      setNuevoCompromiso({
        compromiso: "",
        responsable: "",
        fechaCumplimiento: null,
      });
    }
  };

  // Función para buscar compromisos de actas anteriores
  const fetchPreviousCommitments = async (projectName, productName) => {
    if (!projectName) {
      console.log(
        "No se proporcionó nombre de proyecto, no se buscarán compromisos"
      );
      setPreviousCommitments([]);
      setPreviousOrdenDia([]);
      return;
    }

    setLoadingCommitments(true);
    setLoadingPreviousOrdenDia(true);
    console.log(
      `Buscando compromisos para: Proyecto=${projectName}, Producto=${
        productName || "No seleccionado"
      }`
    );

    try {
      const actasRef = collection(db, "actas");
      let filteredActas = [];

      // Primero obtenemos todas las actas del proyecto
      const q = query(
        actasRef,
        where("tipoProyecto", "==", projectName),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        console.log(
          `Se encontraron ${querySnapshot.size} actas para el proyecto ${projectName}`
        );

        // Convertir todos los documentos a objetos con ID, excluyendo borradores
        const allActas = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Excluir actas que están en estado borrador
          const esBorrador =
            data.approvalStatus === "borrador" || data.isDraft === true;
          if (!esBorrador) {
            data.id = doc.id; // Guardar el ID del documento
            allActas.push(data);
          }
        });

        if (productName && productName.trim() !== "") {
          // Buscar actas por proyecto y producto específico
          console.log(`Filtrando por producto específico: ${productName}`);

          // PRIMERA PRIORIDAD: Actas con exactamente el mismo producto (en array o campo singular)
          allActas.forEach((data) => {
            // Verificar si el producto está en el array de productos
            let productoEnArray = false;
            if (
              data.productosProyecto &&
              Array.isArray(data.productosProyecto)
            ) {
              // Imprimir los productos en el array para depuración
              console.log(
                `Acta ${data.numeroActa} (${
                  data.id
                }) tiene productos: ${JSON.stringify(data.productosProyecto)}`
              );
              // Verificar si algún producto en el array coincide con el producto buscado (comparación normalizada)
              productoEnArray = data.productosProyecto.some((p) => {
                // Normalizar ambos strings para comparación (eliminar espacios extras y convertir a minúsculas)
                const pNormalizado = p ? p.trim() : "";
                const productNameNormalizado = productName
                  ? productName.trim()
                  : "";
                const coincide = pNormalizado === productNameNormalizado;
                if (coincide) {
                  console.log(
                    `  - Coincidencia encontrada: '${pNormalizado}' === '${productNameNormalizado}'`
                  );
                }
                return coincide;
              });
            }

            // Verificar si el producto está en el campo singular
            let productoSingular = false;
            if (data.productoProyecto) {
              console.log(
                `Acta ${data.numeroActa} (${data.id}) tiene producto singular: '${data.productoProyecto}'`
              );
              // Normalizar ambos strings para comparación
              const productoProyectoNormalizado = data.productoProyecto
                ? data.productoProyecto.trim()
                : "";
              const productNameNormalizado = productName
                ? productName.trim()
                : "";
              productoSingular =
                productoProyectoNormalizado === productNameNormalizado;
              if (productoSingular) {
                console.log(
                  `  - Coincidencia encontrada en campo singular: '${productoProyectoNormalizado}' === '${productNameNormalizado}'`
                );
              }
            }

            // Incluir actas que tienen este producto exacto
            if (productoEnArray || productoSingular) {
              console.log(
                `Acta ${data.numeroActa} (${data.id}) incluye el producto exacto: ${productName}`
              );
              filteredActas.push(data);
            }
          });

          // Si no encontramos actas con este producto específico, buscar actas sin productos como respaldo
          if (filteredActas.length === 0) {
            console.log(
              `No se encontraron actas con el producto ${productName}, buscando actas sin productos como respaldo`
            );

            allActas.forEach((data) => {
              // Verificar que NO tenga productos (ni en array ni en campo singular)
              const tieneProductosArray =
                data.productosProyecto &&
                Array.isArray(data.productosProyecto) &&
                data.productosProyecto.length > 0;
              const tieneProductoSingular =
                data.productoProyecto && data.productoProyecto.trim() !== "";

              // Solo incluir actas que NO tienen productos asociados
              if (!tieneProductosArray && !tieneProductoSingular) {
                console.log(
                  `Acta ${data.numeroActa} (${data.id}) no tiene productos asociados (respaldo)`
                );
                filteredActas.push(data);
              }
            });
          }
        } else {
          // Buscar actas solo por proyecto SIN productos asociados
          console.log(
            `Filtrando actas solo por proyecto SIN productos: ${projectName}`
          );

          allActas.forEach((data) => {
            // Verificar que NO tenga productos (ni en array ni en campo singular)
            const tieneProductosArray =
              data.productosProyecto &&
              Array.isArray(data.productosProyecto) &&
              data.productosProyecto.length > 0;
            const tieneProductoSingular =
              data.productoProyecto && data.productoProyecto.trim() !== "";

            // Solo incluir actas que NO tienen productos asociados
            if (!tieneProductosArray && !tieneProductoSingular) {
              console.log(
                `Acta ${data.numeroActa} (${data.id}) no tiene productos asociados`
              );
              filteredActas.push(data);
            }
          });
        }
      }

      console.log(
        `Se encontraron ${filteredActas.length} actas que cumplen con los criterios`
      );

      // Si hay actas filtradas, usar la más reciente
      if (filteredActas.length > 0) {
        // Primero ordenar por relevancia (actas con producto específico primero)
        if (productName && productName.trim() !== "") {
          filteredActas.sort((a, b) => {
            // Verificar si el acta tiene el producto específico
            let aHasProduct = false;
            if (a.productosProyecto && Array.isArray(a.productosProyecto)) {
              // Normalizar la comparación
              const productNameNormalizado = productName
                ? productName.trim()
                : "";
              aHasProduct = a.productosProyecto.some((p) => {
                const pNormalizado = p ? p.trim() : "";
                return pNormalizado === productNameNormalizado;
              });
            }
            if (!aHasProduct && a.productoProyecto) {
              // Normalizar la comparación
              const productoProyectoNormalizado = a.productoProyecto
                ? a.productoProyecto.trim()
                : "";
              const productNameNormalizado = productName
                ? productName.trim()
                : "";
              aHasProduct =
                productoProyectoNormalizado === productNameNormalizado;
            }

            let bHasProduct = false;
            if (b.productosProyecto && Array.isArray(b.productosProyecto)) {
              // Normalizar la comparación
              const productNameNormalizado = productName
                ? productName.trim()
                : "";
              bHasProduct = b.productosProyecto.some((p) => {
                const pNormalizado = p ? p.trim() : "";
                return pNormalizado === productNameNormalizado;
              });
            }
            if (!bHasProduct && b.productoProyecto) {
              // Normalizar la comparación
              const productoProyectoNormalizado = b.productoProyecto
                ? b.productoProyecto.trim()
                : "";
              const productNameNormalizado = productName
                ? productName.trim()
                : "";
              bHasProduct =
                productoProyectoNormalizado === productNameNormalizado;
            }

            // Priorizar actas con el producto específico
            if (aHasProduct && !bHasProduct) return -1;
            if (!aHasProduct && bHasProduct) return 1;
            return 0;
          });
        }

        // Luego ordenar por fecha de creación descendente (la más reciente primero)
        filteredActas.sort((a, b) => {
          // Asegurarse de que createdAt existe y convertirlo a Date
          const dateA = a.createdAt
            ? a.createdAt.toDate
              ? a.createdAt.toDate()
              : new Date(a.createdAt)
            : new Date(0);
          const dateB = b.createdAt
            ? b.createdAt.toDate
              ? b.createdAt.toDate()
              : new Date(b.createdAt)
            : new Date(0);
          return dateB - dateA;
        });

        const latestActa = filteredActas[0];
        console.log(
          `Usando acta más reciente: ${latestActa.numeroActa} (${latestActa.id})`
        );

        // Verificar si el acta tiene el producto específico
        let tieneProductoEspecifico = false;
        if (productName && productName.trim() !== "") {
          // Verificar en el array de productos
          if (
            latestActa.productosProyecto &&
            Array.isArray(latestActa.productosProyecto)
          ) {
            console.log(
              `Verificando si el acta ${latestActa.numeroActa} tiene el producto '${productName}' en su array:`,
              latestActa.productosProyecto
            );
            // Normalizar la comparación
            const productNameNormalizado = productName
              ? productName.trim()
              : "";
            tieneProductoEspecifico = latestActa.productosProyecto.some((p) => {
              // Normalizar el string del producto
              const pNormalizado = p ? p.trim() : "";
              const coincide = pNormalizado === productNameNormalizado;
              if (coincide) {
                console.log(
                  `  - Coincidencia encontrada: '${pNormalizado}' === '${productNameNormalizado}'`
                );
              }
              return coincide;
            });
          }

          // Si no se encontró en el array, verificar en el campo singular
          if (!tieneProductoEspecifico && latestActa.productoProyecto) {
            console.log(
              `Verificando si el acta ${latestActa.numeroActa} tiene el producto '${productName}' en su campo singular: '${latestActa.productoProyecto}'`
            );
            // Normalizar ambos strings para comparación
            const productoProyectoNormalizado = latestActa.productoProyecto
              ? latestActa.productoProyecto.trim()
              : "";
            const productNameNormalizado = productName
              ? productName.trim()
              : "";
            tieneProductoEspecifico =
              productoProyectoNormalizado === productNameNormalizado;
            if (tieneProductoEspecifico) {
              console.log(
                `  - Coincidencia encontrada en campo singular: '${productoProyectoNormalizado}' === '${productNameNormalizado}'`
              );
            }
          }
        }

        if (productName && productName.trim() !== "") {
          if (tieneProductoEspecifico) {
            console.log(
              `Usando acta más reciente CON el producto específico: ${latestActa.numeroActa} (${latestActa.id}) - Producto: ${productName}`
            );
            if (
              latestActa.productosProyecto &&
              Array.isArray(latestActa.productosProyecto)
            ) {
              console.log(
                `  - Productos en el acta: ${JSON.stringify(
                  latestActa.productosProyecto
                )}`
              );
            }
            if (latestActa.productoProyecto) {
              console.log(
                `  - Producto singular en el acta: ${latestActa.productoProyecto}`
              );
            }
          } else {
            console.log(
              `Usando acta más reciente SIN productos como respaldo: ${latestActa.numeroActa} (${latestActa.id}) - Producto buscado: ${productName}`
            );
          }
        } else {
          console.log(
            `Usando acta más reciente: ${latestActa.numeroActa} (${latestActa.id})`
          );
        }

        // Verificar si hay compromisos en el acta más reciente
        if (latestActa.compromisos && latestActa.compromisos.length > 0) {
          // Convertir las fechas de Firestore a objetos Date
          const commitments = latestActa.compromisos.map((c) => {
            // Verificar si fechaCumplimiento existe y convertirla a Date
            let fechaCumplimiento;
            try {
              fechaCumplimiento =
                c.fechaCumplimiento &&
                (c.fechaCumplimiento.toDate
                  ? c.fechaCumplimiento.toDate()
                  : new Date(c.fechaCumplimiento));
            } catch (err) {
              console.error("Error al convertir fecha:", err);
              fechaCumplimiento = new Date(); // Fecha por defecto
            }

            return {
              ...c,
              fechaCumplimiento,
              completed: false, // Agregar estado para el checkbox
              fromPreviousActa: true, // Marcar como proveniente de acta anterior
              fromActaNumero: latestActa.numeroActa, // Agregar número de acta de origen
              fromActaId: latestActa.id, // Agregar ID del acta de origen para referencia
            };
          });

          console.log(
            `Se encontraron ${commitments.length} compromisos pendientes del acta anterior:`,
            commitments.map((c) => c.compromiso)
          );

          // Actualizar el estado con los compromisos encontrados
          setPreviousCommitments(commitments);
        } else {
          console.log("No se encontraron compromisos en el acta más reciente");
          setPreviousCommitments([]);
        }

        // Verificar si hay puntos en el orden del día en el acta más reciente
        if (latestActa.ordenDia && latestActa.ordenDia.length > 0) {
          console.log(
            `Se encontraron ${latestActa.ordenDia.length} puntos del orden del día del acta anterior`
          );
          // Agregar información del acta de origen a cada punto del orden del día
          const ordenDiaConOrigen = latestActa.ordenDia.map((item) => ({
            ...item,
            fromPreviousActa: true,
            fromActaNumber: latestActa.numeroActa,
          }));
          // Actualizar el estado con los puntos del orden del día encontrados
          setPreviousOrdenDia(ordenDiaConOrigen);
        } else {
          console.log(
            "No se encontraron puntos en el orden del día del acta más reciente"
          );
          setPreviousOrdenDia([]);
        }
      } else {
        // No se encontraron actas que cumplan con los criterios
        console.log("No se encontraron actas que cumplan con los criterios");
        setPreviousCommitments([]);
        setPreviousOrdenDia([]);
      }
    } catch (error) {
      console.error("Error al buscar compromisos anteriores:", error);

      // Verificar si es un error de índice de Firebase
      if (
        error.code === "failed-precondition" &&
        error.message &&
        error.message.includes("index")
      ) {
        const indexUrlMatch = error.message.match(
          /https:\/\/console\.firebase\.google\.com[^\s]+/
        );
        const indexUrl = indexUrlMatch ? indexUrlMatch[0] : null;
        window.missingIndexUrl = indexUrl;
        console.log("Se requiere crear un índice en Firebase. URL:", indexUrl);
      }

      // Limpiar los compromisos anteriores en caso de error
      setPreviousCommitments([]);
      setPreviousOrdenDia([]);
    } finally {
      setLoadingCommitments(false);
      setLoadingPreviousOrdenDia(false);
    }
  };

  // Función para manejar el cambio en el estado de compromisos anteriores
  const handlePreviousCommitmentChange = (index, nuevoEstado) => {
    setPreviousCommitments((prev) => {
      const updated = [...prev];
      updated[index].estado = nuevoEstado;
      return updated;
    });
  };

  const handleAgregarAsistente = () => {
    if (nuevoAsistente.nombre && nuevoAsistente.cargo) {
      setFormData((prev) => ({
        ...prev,
        asistentes: [...prev.asistentes, nuevoAsistente],
      }));
      setNuevoAsistente({
        nombre: "",
        cargo: "",
        institucion: "",
        asistio: "Presencial",
        email: "",
        firma: "",
      });
    }
  };

  const handleEliminarCompromiso = (index) => {
    setFormData((prev) => ({
      ...prev,
      compromisos: prev.compromisos.filter((_, i) => i !== index),
    }));
  };

  const handleEliminarAsistente = (index) => {
    setFormData((prev) => ({
      ...prev,
      asistentes: prev.asistentes.filter((_, i) => i !== index),
    }));
  };

  // Funciones para editar compromisos
  const handleEditarCompromiso = (index) => {
    const compromiso = formData.compromisos[index];
    setTempCompromiso({
      compromiso: compromiso.compromiso,
      responsable: compromiso.responsable,
      fechaCumplimiento: compromiso.fechaCumplimiento,
    });
    setEditingCompromisoIndex(index);
  };

  const handleGuardarCompromiso = () => {
    if (tempCompromiso.compromiso && tempCompromiso.responsable) {
      setFormData((prev) => ({
        ...prev,
        compromisos: prev.compromisos.map((item, index) =>
          index === editingCompromisoIndex ? tempCompromiso : item
        ),
      }));
      setEditingCompromisoIndex(null);
      setTempCompromiso({
        compromiso: "",
        responsable: "",
        fechaCumplimiento: null,
      });
    }
  };

  const handleCancelarCompromiso = () => {
    setEditingCompromisoIndex(null);
    setTempCompromiso({
      compromiso: "",
      responsable: "",
      fechaCumplimiento: null,
    });
  };

  const handleTempCompromisoChange = (e) => {
    const { name, value } = e.target;
    setTempCompromiso((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Funciones para editar asistentes
  const handleEditarAsistente = (index) => {
    const asistente = formData.asistentes[index];
    setTempAsistente({
      nombre: asistente.nombre,
      cargo: asistente.cargo,
      institucion: asistente.institucion || "",
      asistio: asistente.asistio,
      email: asistente.email || "",
      firma: asistente.firma || "",
    });
    setEditingAsistenteIndex(index);
  };

  const handleGuardarAsistente = () => {
    if (tempAsistente.nombre && tempAsistente.cargo) {
      setFormData((prev) => ({
        ...prev,
        asistentes: prev.asistentes.map((item, index) =>
          index === editingAsistenteIndex ? tempAsistente : item
        ),
      }));
      setEditingAsistenteIndex(null);
      setTempAsistente({
        nombre: "",
        cargo: "",
        institucion: "",
        asistio: "Presencial",
        email: "",
        firma: "",
      });
    }
  };

  const handleCancelarAsistente = () => {
    setEditingAsistenteIndex(null);
    setTempAsistente({
      nombre: "",
      cargo: "",
      institucion: "",
      asistio: "Presencial",
      email: "",
      firma: "",
    });
  };

  const handleTempAsistenteChange = (e) => {
    const { name, value } = e.target;
    setTempAsistente((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Funciones para editar número de acta
  const handleEditarNumeroActa = () => {
    setTempNumeroActa(formData.numeroActa);
    setEditingNumeroActa(true);
    setNumeroActaError("");
  };

  const validateNumeroActa = async (numeroActa) => {
    if (!numeroActa || numeroActa.trim() === "") {
      return "El número de acta es requerido";
    }

    // Validar que sea un número válido (entero, decimal, positivo o negativo)
    const numero = parseFloat(numeroActa);
    if (isNaN(numero)) {
      return "El número de acta debe ser un número válido (entero, decimal, positivo o negativo)";
    }

    // Verificar si el número ya existe para este proyecto/producto específico
    try {
      const actasRef = collection(db, "actas");
      const querySnapshot = await getDocs(
        query(actasRef, where("numeroActa", "==", numeroActa))
      );

      // Filtrar actas que tengan EXACTAMENTE la misma combinación de proyectos y productos
      const proyectosSeleccionados = formData.tipoProyectos
        ? [...formData.tipoProyectos].sort()
        : [];
      const productosSeleccionados = formData.productosProyecto
        ? [...formData.productosProyecto].sort()
        : [];

      const existingActas = querySnapshot.docs.filter((doc) => {
        // Si estamos editando, excluir el acta actual
        if (editingActa && editingActa.id && doc.id === editingActa.id) {
          return false;
        }

        const data = doc.data();
        const actaProyectos = data.tipoProyectos
          ? [...data.tipoProyectos].sort()
          : [];
        const actaProductos = data.productosProyecto
          ? [...data.productosProyecto].sort()
          : [];

        // Verificar si es exactamente la misma combinación de proyectos y productos
        const mismaCombinaciónProyectos =
          JSON.stringify(actaProyectos) ===
          JSON.stringify(proyectosSeleccionados);
        const mismaCombinaciónProductos =
          JSON.stringify(actaProductos) ===
          JSON.stringify(productosSeleccionados);

        return mismaCombinaciónProyectos && mismaCombinaciónProductos;
      });

      if (existingActas.length > 0) {
        return `El número de acta ${numeroActa} ya existe para esta combinación específica de proyectos/productos`;
      }
    } catch (error) {
      console.error("Error validando número de acta:", error);
      return "Error al validar el número de acta";
    }

    return null; // Sin errores
  };

  const handleGuardarNumeroActa = async () => {
    const error = await validateNumeroActa(tempNumeroActa);
    if (error) {
      setNumeroActaError(error);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      numeroActa: tempNumeroActa,
    }));
    setEditingNumeroActa(false);
    setTempNumeroActa("");
    setNumeroActaError("");
  };

  const handleCancelarNumeroActa = () => {
    setEditingNumeroActa(false);
    setTempNumeroActa("");
    setNumeroActaError("");
  };

  const handleOrdenItemChange = (e) => {
    const { name, value } = e.target;
    setNuevoOrdenItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAgregarOrdenItem = () => {
    if (nuevoOrdenItem.titulo && nuevoOrdenItem.descripcion) {
      setFormData((prev) => ({
        ...prev,
        ordenDia: [...prev.ordenDia, nuevoOrdenItem],
      }));
      setNuevoOrdenItem({
        titulo: "",
        descripcion: "",
      });
    }
  };

  // Función para manejar la carga de archivo de transcripción
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar que sea un archivo soportado (.txt, .vtt, .docx)
    const fileName = file.name.toLowerCase();
    const isValidFile =
      fileName.endsWith(".txt") ||
      fileName.endsWith(".vtt") ||
      fileName.endsWith(".docx") ||
      file.type.includes("text");

    if (!isValidFile) {
      setAiError("Por favor, selecciona un archivo válido (.txt, .vtt, .docx)");
      setShowAiAlert(true);
      return;
    }

    setTranscriptionFile(file);

    // Verificar si hay archivos listos para procesar
    setReadyToProcess(true); // Se puede procesar solo con transcripción

    // Limpiar el input de archivo
    event.target.value = "";
  };

  // Función para manejar la carga de archivo CSV de asistencia
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar que sea un archivo CSV
    const fileName = file.name.toLowerCase();
    const isValidFile = fileName.endsWith(".csv") || file.type.includes("csv");

    if (!isValidFile) {
      setAiError("Por favor, selecciona un archivo CSV válido");
      setShowAiAlert(true);
      return;
    }

    setCsvFile(file);

    // Verificar si hay archivos listos para procesar
    setReadyToProcess(true); // Se puede procesar solo con CSV

    // Limpiar el input de archivo
    event.target.value = "";
  };

  // Función para iniciar el procesamiento manual
  const handleStartProcessing = async () => {
    // Verificar si hay datos significativos en el formulario
    if (hasSignificantData(formData)) {
      const confirmOverwrite = window.confirm(
        "¿Está seguro de usar la IA? Esto reemplazará la información que ya ha escrito manualmente en el formulario."
      );

      if (!confirmOverwrite) {
        return; // No proceder si el usuario cancela
      }
    }

    if (transcriptionFile && csvFile) {
      await processFilesWithAI(transcriptionFile, csvFile);
    } else if (transcriptionFile) {
      await processSingleTranscription(transcriptionFile);
    } else if (csvFile) {
      await processCsvOnly(csvFile);
    }
    setReadyToProcess(false);
  };

  // Función para procesar solo la transcripción
  const processSingleTranscription = async (file) => {
    setAiLoading(true);
    setAiError("");
    setAiSuccess("");

    try {
      let text = "";
      const fileName = file.name.toLowerCase();

      // Leer el contenido según el tipo de archivo
      if (fileName.endsWith(".txt") || fileName.endsWith(".vtt")) {
        text = await file.text();
      } else if (fileName.endsWith(".docx")) {
        // Leer archivos .docx usando mammoth.js
        try {
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          text = result.value;

          if (result.messages && result.messages.length > 0) {
            console.warn(
              "Advertencias al leer el archivo .docx:",
              result.messages
            );
          }
        } catch (docxError) {
          console.error("Error al leer archivo .docx:", docxError);
          throw new Error(
            "No se pudo leer el archivo .docx. Asegúrate de que sea un archivo válido de Word."
          );
        }
      }

      if (!text.trim()) {
        throw new Error("El archivo está vacío");
      }

      // Analizar la transcripción con el servicio unificado de IA (MiniMax o Gemini)
      const aiResult = await unifiedAnalyzeTranscription(text, fileName);
      const extractedData = aiResult.data;
      setAiMetricsInfo(aiResult.metrics);

      // Convertir fechas y horas a objetos Date
      const convertedData = convertDatesToObjects(extractedData);

      // Actualizar el formulario con los datos extraídos
      setFormData((prev) => ({
        ...prev,
        tipoActa: convertedData.tipoActa || prev.tipoActa,
        fecha: convertedData.fecha || prev.fecha,
        horaInicio: convertedData.horaInicio || prev.horaInicio,
        horaFinal: convertedData.horaFinal || prev.horaFinal,
        lugar: convertedData.lugar || prev.lugar,
        objetivo: convertedData.objetivo || prev.objetivo,
        responsable: convertedData.responsable || prev.responsable,
        ordenDia:
          convertedData.ordenDia.length > 0
            ? convertedData.ordenDia
            : prev.ordenDia,
        compromisos:
          convertedData.compromisos.length > 0
            ? convertedData.compromisos
            : prev.compromisos,
        asistentes:
          convertedData.asistentes.length > 0
            ? convertedData.asistentes
            : prev.asistentes,
      }));

      const modelLabel = aiResult.metrics?.provider === 'MiniMax' ? ` (${aiResult.metrics.model})` : '';
      setAiSuccess(
        `¡Transcripción ${fileName
          .split(".")
          .pop()
          .toUpperCase()} analizada exitosamente${modelLabel}! Los campos han sido completados automáticamente.`
      );
      setShowAiAlert(true);
    } catch (error) {
      console.error("Error al procesar la transcripción:", error);
      setAiError("Error al analizar la transcripción: " + error.message);
      setShowAiAlert(true);
    } finally {
      setAiLoading(false);
    }
  };

  // Función para procesar solo CSV
  const processCsvOnly = async (file) => {
    setCsvLoading(true);
    setAiError("");
    setAiSuccess("");

    try {
      // Leer el contenido del CSV
      const csvText = await file.text();

      if (!csvText.trim()) {
        throw new Error("El archivo CSV está vacío");
      }

      // Analizar solo el CSV con el servicio unificado de IA
      const aiResult = await unifiedAnalyzeCsvOnly(csvText, file.name);
      const extractedData = aiResult.data;
      setAiMetricsInfo(aiResult.metrics);

      // Actualizar solo los asistentes en el formulario
      setFormData((prev) => ({
        ...prev,
        asistentes:
          extractedData.asistentes.length > 0
            ? extractedData.asistentes
            : prev.asistentes,
      }));

      const modelLabel = aiResult.metrics?.provider === 'MiniMax' ? ` (${aiResult.metrics.model})` : '';
      setAiSuccess(
        `¡Lista de asistencia CSV analizada exitosamente${modelLabel}! Los asistentes han sido registrados automáticamente.`
      );
      setShowAiAlert(true);
    } catch (error) {
      console.error("Error al procesar el CSV:", error);
      setAiError("Error al analizar el CSV: " + error.message);
      setShowAiAlert(true);
    } finally {
      setCsvLoading(false);
    }
  };

  // Función para procesar ambos archivos (transcripción + CSV)
  const processFilesWithAI = async (transcriptionFile, csvFile) => {
    setAiLoading(true);
    setAiError("");
    setAiSuccess("");

    try {
      // Leer transcripción
      let transcriptionText = "";
      const transcriptionFileName = transcriptionFile.name.toLowerCase();

      if (
        transcriptionFileName.endsWith(".txt") ||
        transcriptionFileName.endsWith(".vtt")
      ) {
        transcriptionText = await transcriptionFile.text();
      } else if (transcriptionFileName.endsWith(".docx")) {
        try {
          const arrayBuffer = await transcriptionFile.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          transcriptionText = result.value;
        } catch (docxError) {
          throw new Error("No se pudo leer el archivo .docx de transcripción.");
        }
      }

      // Leer CSV
      const csvText = await csvFile.text();

      if (!transcriptionText.trim()) {
        throw new Error("El archivo de transcripción está vacío");
      }

      if (!csvText.trim()) {
        throw new Error("El archivo CSV está vacío");
      }

      // Analizar ambos archivos con el servicio unificado de IA
      const aiResult = await unifiedAnalyzeTranscriptionWithAttendance(
        transcriptionText,
        csvText,
        transcriptionFile.name,
        csvFile.name
      );
      const extractedData = aiResult.data;
      setAiMetricsInfo(aiResult.metrics);

      // Convertir fechas y horas a objetos Date
      const convertedData = convertDatesToObjects(extractedData);

      // Actualizar el formulario con los datos extraídos
      setFormData((prev) => ({
        ...prev,
        tipoActa: convertedData.tipoActa || prev.tipoActa,
        fecha: convertedData.fecha || prev.fecha,
        horaInicio: convertedData.horaInicio || prev.horaInicio,
        horaFinal: convertedData.horaFinal || prev.horaFinal,
        lugar: convertedData.lugar || prev.lugar,
        objetivo: convertedData.objetivo || prev.objetivo,
        responsable: convertedData.responsable || prev.responsable,
        ordenDia:
          convertedData.ordenDia.length > 0
            ? convertedData.ordenDia
            : prev.ordenDia,
        compromisos:
          convertedData.compromisos.length > 0
            ? convertedData.compromisos
            : prev.compromisos,
        asistentes:
          convertedData.asistentes.length > 0
            ? convertedData.asistentes
            : prev.asistentes,
      }));

      const modelLabel = aiResult.metrics?.provider === 'MiniMax' ? ` (${aiResult.metrics.model})` : '';
      setAiSuccess(
        `¡Transcripción y lista de asistencia analizadas exitosamente${modelLabel}! Los campos han sido completados automáticamente.`
      );
      setShowAiAlert(true);

      // Limpiar archivos después del procesamiento exitoso
      setCsvFile(null);
      setTranscriptionFile(null);
    } catch (error) {
      console.error("Error al procesar los archivos:", error);
      setAiError("Error al analizar los archivos: " + error.message);
      setShowAiAlert(true);
    } finally {
      setAiLoading(false);
    }
  };

  // Función para limpiar el formulario
  const handleClearForm = () => {
    if (window.confirm("¿Está seguro de limpiar el formulario?")) {
      setFormData({
        tipoActa: "",
        tipoActaCustom: "",
        comiteId: "",
        tipoProyectos: [],
        tipoProyectoCustom: "",
        productosProyecto: [],
        numeroActa: nextActaNumber.toString(),
        fecha: new Date(),
        horaInicio: null,
        horaFinal: null,
        lugar: "",
        objetivo: "",
        ordenDia: [],
        compromisos: [],
        asistentes: [],
        responsable: "",
        imagenesAdjuntas: [],
        estadoActa: "en_curso",
        approvalStatus: "por aprobar",
      });

      // Limpiar estados adicionales
      setNuevoOrdenItem({ titulo: "", descripcion: "" });
      setNuevoCompromiso({
        compromiso: "",
        responsable: "",
        fechaCumplimiento: null,
      });
      setNuevoAsistente({
        nombre: "",
        cargo: "",
        institucion: "",
        asistio: "Presencial",
        email: "",
        firma: "",
      });
      setSelectedProject(null);
      setProductoSeleccionado("");
      setProyectoSeleccionado("");
      setPreviousCommitments([]);
      setPreviousOrdenDia([]);

      // Limpiar archivos de IA
      setCsvFile(null);
      setTranscriptionFile(null);
      setCsvLoading(false);
      setAiLoading(false);
      setReadyToProcess(false);

      // Mostrar mensaje de confirmación
      setAiSuccess("Formulario limpiado exitosamente");
      setShowAiAlert(true);
    }
  };

  // Función para cerrar las alertas
  const handleCloseAlert = () => {
    setShowAiAlert(false);
    setAiError("");
    setAiSuccess("");
  };

  const handleEliminarOrdenItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      ordenDia: prev.ordenDia.filter((_, i) => i !== index),
    }));
  };

  // Funciones para editar elementos de Orden del día
  const handleEditarOrdenItem = (index) => {
    const item = formData.ordenDia[index];
    setTempOrdenItem({ titulo: item.titulo, descripcion: item.descripcion });
    setEditingOrdenIndex(index);
  };

  const handleGuardarOrdenItem = () => {
    if (tempOrdenItem.titulo && tempOrdenItem.descripcion) {
      setFormData((prev) => ({
        ...prev,
        ordenDia: prev.ordenDia.map((item, index) =>
          index === editingOrdenIndex ? tempOrdenItem : item
        ),
      }));
      setEditingOrdenIndex(null);
      setTempOrdenItem({ titulo: "", descripcion: "" });
    }
  };

  const handleCancelarOrdenItem = () => {
    setEditingOrdenIndex(null);
    setTempOrdenItem({ titulo: "", descripcion: "" });
  };

  const handleTempOrdenItemChange = (e) => {
    const { name, value } = e.target;
    setTempOrdenItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevenir múltiples envíos
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Establecer que se ha intentado enviar el formulario
      window.attemptedSubmit = true;

      // Validar que se haya agregado al menos un tema en la orden del día
      if (formData.ordenDia.length === 0) {
        alert(
          "Debe agregar al menos un tema en la Orden del día antes de generar el acta."
        );
        return;
      }

      // Validar que se haya seleccionado un comité si el tipo de acta es COMITE
      if (formData.tipoActa === "COMITE") {
        if (!formData.comiteId) {
          alert("Debe seleccionar un comité para este tipo de acta.");
          return;
        }
        if (formData.comiteId === "__NUEVO__" && !nuevoComiteNombre.trim()) {
          alert("Debe ingresar el nombre del nuevo comité.");
          return;
        }
      }

      // Validar categoría si el tipo de acta es OTRO
      if (formData.tipoActa === "OTRO") {
        if (!formData.tipoActaCustom) {
          alert("Debe seleccionar una categoría para el tipo de acta Otro.");
          return;
        }
        if (formData.tipoActaCustom === "__NUEVO__" && !nuevaCategoriaOtroNombre.trim()) {
          alert("Debe ingresar el nombre de la nueva categoría.");
          return;
        }
      }

      // Validar que se haya seleccionado al menos un proyecto (solo si no es tipo COMITE ni OTRO sin vinculación)
      const isExempt = (formData.tipoActa === "COMITE" || formData.tipoActa === "OTRO") && !vinculaContratoProyecto;

      if (
        !isExempt &&
        formData.tipoActa !== "COMITE" &&
        formData.tipoProyectos.length === 0 &&
        !formData.tipoProyecto
      ) {
        alert("Debe seleccionar al menos un proyecto.");
        return;
      }

      // Validar campos obligatorios para REUNION DE SEGUIMIENTO
      if (formData.tipoActa === "REUNION") {
        if (formData.tipoProyectos.length === 0) {
          alert(
            "Para reuniones de seguimiento, debe seleccionar al menos un contrato."
          );
          return;
        }
        if (formData.productosProyecto.length === 0) {
          alert(
            "Para reuniones de seguimiento, debe seleccionar al menos un proyecto."
          );
          return;
        }
      }

      // Validar que SIEMPRE se seleccione tanto contrato como proyecto (obligatorio)
      // EXCEPTO si es COMITE o OTRO y no vincula contrato/proyecto
      const skipProjectValidation = (formData.tipoActa === "COMITE" || formData.tipoActa === "OTRO") && !vinculaContratoProyecto;
      
      if (!skipProjectValidation) {
        if (formData.tipoProyectos.length === 0) {
          alert("Debe seleccionar al menos un contrato.");
          return;
        }
        if (formData.productosProyecto.length === 0) {
          alert("Debe seleccionar al menos un proyecto.");
          return;
        }
      }

      // Remover la validación de encargados de aprobación en la creación
      // Los encargados de aprobación se asignarán al momento de aprobar el acta

      // Preparar los datos de envío antes de solicitar el responsable
      const submissionDataPrep = await prepareSubmissionData();

      // Enviar el acta directamente
      await onSubmit(submissionDataPrep);
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error al preparar el acta:", error);
      alert("Error al preparar el acta. Por favor, intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Verificar si se está convirtiendo de borrador a acta real
  const isConvertingFromDraft =
    editingActa &&
    (editingActa.approvalStatus === "borrador" || editingActa.isDraft);

  // Función para preparar los datos de envío
  const prepareSubmissionData = async () => {
    // Agregar los compromisos en curso de actas anteriores a los compromisos actuales
    // Solo incluir los compromisos con estado 'En curso', los marcados como 'Terminado' no se incluyen
    const uncompletedCommitments = previousCommitments
      .filter((commitment) => commitment.estado === "En curso")
      .map(({ compromiso, responsable, fechaCumplimiento, estado }) => ({
        compromiso,
        responsable,
        fechaCumplimiento,
        estado,
        fromPreviousActa: true, // Marcar que proviene de acta anterior
      }));

    // Primero los compromisos no completados de actas anteriores, luego los nuevos
    // Solo incluir compromisos anteriores si no estamos en modo edición
    const allCommitments = isEditMode
      ? formData.compromisos
      : [...uncompletedCommitments, ...formData.compromisos];

    // Obtener y persistir el nombre del comité si es necesario
    let actualComiteId = formData.comiteId;
    let nombreComite = "";
    if (formData.tipoActa === "COMITE") {
      if (formData.comiteId === "__NUEVO__") {
        const saved = await saveNewCommitteeIfNeeded(formData.comiteId, nuevoComiteNombre);
        actualComiteId = saved.id;
        nombreComite = saved.nombre;
        setFormData((prev) => ({ ...prev, comiteId: saved.id }));
      } else if (formData.comiteId) {
        const comiteSeleccionado = comites.find((c) => c.id === formData.comiteId);
        if (comiteSeleccionado) {
          nombreComite = comiteSeleccionado.nombre;
        }
      }
    }

    // Persistir nueva categoría OTRO si fue ingresada manualmente
    let actualTipoActaCustom = formData.tipoActaCustom;
    if (formData.tipoActa === "OTRO") {
      if (formData.tipoActaCustom === "__NUEVO__") {
        const savedCat = await saveNewCategoryOtherIfNeeded(formData.tipoActaCustom, nuevaCategoriaOtroNombre);
        actualTipoActaCustom = savedCat.nombre;
        setFormData((prev) => ({ ...prev, tipoActaCustom: savedCat.nombre }));
      }
    }

    // Determinar el tipo de proyecto a enviar
    let tipoProyecto;

    const isComiteType = formData.tipoActa === 'COMITE' || (typeof formData.tipoActa === 'string' && formData.tipoActa.startsWith('COMITE'));
    const isOtroType = formData.tipoActa === 'OTRO' || (typeof formData.tipoActa === 'string' && formData.tipoActa !== 'REUNION' && !formData.tipoActa.startsWith('COMITE'));
    
    const isDirecto = (isComiteType || isOtroType) && 
                     (!formData.tipoProyectos || formData.tipoProyectos.length === 0 || 
                      (formData.tipoProyectos.length === 1 && formData.tipoProyectos[0] === 'sin_proyecto'));

    if (isDirecto) {
      tipoProyecto = formData.tipoActa === 'OTRO' 
        ? (actualTipoActaCustom || 'OTRO') 
        : (nombreComite || formData.tipoActa);
    } else if (
      formData.tipoProyectos &&
      formData.tipoProyectos.length > 0 &&
      formData.productosProyecto &&
      formData.productosProyecto.length > 0
    ) {
      tipoProyecto = "combinado";
    } else if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
      // Fallback para casos donde solo hay contratos (aunque ahora es obligatorio tener ambos)
      const primerProyectoId = formData.tipoProyectos[0];

      if (primerProyectoId === "OTRO") {
        tipoProyecto =
          formData.tipoProyectoCustom || "Proyecto sin especificar";
      } else {
        const proyecto = projects.find((p) => p.id === primerProyectoId);
        tipoProyecto = proyecto ? proyecto.name : primerProyectoId;
      }
    } else if (formData.tipoProyecto) {
      // Usar el campo tipoProyecto tradicional si existe
      tipoProyecto =
        formData.tipoProyecto === "OTRO"
          ? formData.tipoProyectoCustom
          : selectedProject
          ? selectedProject.name
          : formData.tipoProyecto;
    } else {
      // Valor por defecto para evitar undefined
      tipoProyecto = "Proyecto sin especificar";
    }

    const submissionData = {
      ...formData,
      compromisos: allCommitments,
      objetivo:
        typeof formData.objetivo === "object" && formData.objetivo !== null
          ? JSON.stringify(formData.objetivo)
          : formData.objetivo,
      tipoActa:
        formData.tipoActa === "OTRO"
          ? actualTipoActaCustom
          : formData.tipoActa === "COMITE" && nombreComite
          ? `COMITE - ${nombreComite}`
          : formData.tipoActa,
      tipoActaCustom: actualTipoActaCustom,
      tipoProyecto: tipoProyecto,
      // Mantener el ID del comité para referencia futura
      comiteId: formData.tipoActa === "COMITE" ? actualComiteId : "",
      // No incluir encargados de aprobación en la creación
      // Se asignarán al momento de aprobar el acta
      // Asegurar que el estado de aprobación esté definido
      // Si era un borrador, cambiar a 'por aprobar' al enviar
      approvalStatus:
        formData.approvalStatus === "borrador"
          ? "por aprobar"
          : formData.approvalStatus || "por aprobar",
      isDraft: false, // Ya no es borrador al enviar para aprobación
      // Si se está convirtiendo de borrador, usar el nextActaNumber en lugar del número temporal
      numeroActa: isConvertingFromDraft
        ? nextActaNumber.toString()
        : formData.numeroActa,
    };

    return submissionData;
  };

  // Función para guardar como borrador
  const handleSaveDraft = async (e) => {
    e.preventDefault();

    // Prevenir múltiples envíos
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Validaciones mínimas para borrador
      if (!formData.tipoActa) {
        alert("Debe seleccionar un tipo de acta.");
        return;
      }

      if (formData.tipoActa === "COMITE") {
        if (!formData.comiteId) {
          alert("Debe seleccionar un comité para este tipo de acta.");
          return;
        }
        if (formData.comiteId === "__NUEVO__" && !nuevoComiteNombre.trim()) {
          alert("Debe ingresar el nombre del nuevo comité.");
          return;
        }
      }

      if (formData.tipoActa === "OTRO") {
        if (!formData.tipoActaCustom) {
          alert("Debe seleccionar o ingresar una categoría para el tipo de acta Otro.");
          return;
        }
        if (formData.tipoActaCustom === "__NUEVO__" && !nuevaCategoriaOtroNombre.trim()) {
          alert("Debe ingresar el nombre de la nueva categoría.");
          return;
        }
      }

      // Persistir nuevo comité si fue ingresado manualmente
      let actualComiteId = formData.comiteId;
      let nombreComite = "";
      if (formData.tipoActa === "COMITE") {
        if (formData.comiteId === "__NUEVO__") {
          const saved = await saveNewCommitteeIfNeeded(formData.comiteId, nuevoComiteNombre);
          actualComiteId = saved.id;
          nombreComite = saved.nombre;
          setFormData((prev) => ({ ...prev, comiteId: saved.id }));
        } else if (formData.comiteId) {
          const comiteSeleccionado = comites.find((c) => c.id === formData.comiteId);
          if (comiteSeleccionado) {
            nombreComite = comiteSeleccionado.nombre;
          }
        }
      }

      // Persistir nueva categoría OTRO si fue ingresada manualmente
      let actualTipoActaCustom = formData.tipoActaCustom;
      if (formData.tipoActa === "OTRO") {
        if (formData.tipoActaCustom === "__NUEVO__") {
          const savedCat = await saveNewCategoryOtherIfNeeded(formData.tipoActaCustom, nuevaCategoriaOtroNombre);
          actualTipoActaCustom = savedCat.nombre;
          setFormData((prev) => ({ ...prev, tipoActaCustom: savedCat.nombre }));
        }
      }

      // Determinar el tipo de proyecto a enviar
      let tipoProyecto;

      const isComiteType = formData.tipoActa === 'COMITE' || (typeof formData.tipoActa === 'string' && formData.tipoActa.startsWith('COMITE'));
      const isOtroType = formData.tipoActa === 'OTRO' || (typeof formData.tipoActa === 'string' && formData.tipoActa !== 'REUNION' && !formData.tipoActa.startsWith('COMITE'));
      
      const isDirecto = (isComiteType || isOtroType) && 
                       (!formData.tipoProyectos || formData.tipoProyectos.length === 0 || 
                        (formData.tipoProyectos.length === 1 && formData.tipoProyectos[0] === 'sin_proyecto'));

      if (isDirecto) {
        tipoProyecto = formData.tipoActa === 'OTRO' 
          ? (actualTipoActaCustom || 'OTRO') 
          : (nombreComite || formData.tipoActa);
      } else if (
        formData.tipoProyectos &&
        formData.tipoProyectos.length > 0 &&
        formData.productosProyecto &&
        formData.productosProyecto.length > 0
      ) {
        tipoProyecto = "combinado";
      } else if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
        // Fallback para casos donde solo hay contratos
        const primerProyectoId = formData.tipoProyectos[0];

        if (primerProyectoId === "OTRO") {
          tipoProyecto =
            formData.tipoProyectoCustom || "Proyecto sin especificar";
        } else {
          const proyecto = projects.find((p) => p.id === primerProyectoId);
          tipoProyecto = proyecto ? proyecto.name : primerProyectoId;
        }
      } else if (formData.tipoProyecto) {
        tipoProyecto =
          formData.tipoProyecto === "OTRO"
            ? formData.tipoProyectoCustom
            : selectedProject
            ? selectedProject.name
            : formData.tipoProyecto;
      } else {
        tipoProyecto = "Proyecto sin especificar";
      }

      const draftData = {
        ...formData,
        objetivo:
          typeof formData.objetivo === "object" && formData.objetivo !== null
            ? JSON.stringify(formData.objetivo)
            : formData.objetivo,
        tipoActa:
          formData.tipoActa === "OTRO"
            ? actualTipoActaCustom
            : formData.tipoActa === "COMITE" && nombreComite
            ? `COMITE - ${nombreComite}`
            : formData.tipoActa,
        tipoActaCustom: actualTipoActaCustom,
        tipoProyecto: tipoProyecto,
        comiteId: formData.tipoActa === "COMITE" ? actualComiteId : "",
        // Incluir encargados de aprobación
        encargadosAprobacion: formData.encargadosAprobacion || [],
        approvalStatus: "borrador",
        isDraft: true,
      };

      await onSubmit(draftData, setIsSubmitting);

      // No limpiar caché para borradores, permitir continuar editando
    } catch (error) {
      console.error("Error al guardar el borrador:", error);
      alert("Error al guardar el borrador. Por favor, intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para manejar la previsualización del PDF
  const handlePreview = () => {
    // Validar que se haya agregado al menos un tema en la orden del día
    if (formData.ordenDia.length === 0) {
      alert(
        "Debe agregar al menos un tema en la Orden del día antes de previsualizar el acta."
      );
      return;
    }

    // Validar que se haya seleccionado un comité si el tipo de acta es COMITE
    if (formData.tipoActa === "COMITE") {
      if (!formData.comiteId) {
        alert("Debe seleccionar un comité para este tipo de acta.");
        return;
      }
      if (formData.comiteId === "__NUEVO__" && !nuevoComiteNombre.trim()) {
        alert("Debe ingresar el nombre del nuevo comité.");
        return;
      }
    }

    // Validar categoría si el tipo de acta es OTRO
    if (formData.tipoActa === "OTRO") {
      if (!formData.tipoActaCustom) {
        alert("Debe seleccionar una categoría para el tipo de acta Otro.");
        return;
      }
      if (formData.tipoActaCustom === "__NUEVO__" && !nuevaCategoriaOtroNombre.trim()) {
        alert("Debe ingresar el nombre de la nueva categoría.");
        return;
      }
    }

    // Abrir el modal de previsualización (sin bloquear por proyecto para permitir revisar el formato)
    setShowPreviewModal(true);
  };

  // Función para manejar la subida de actas externas
  const handleUploadExternalActa = async () => {
    try {
      setUploadLoading(true);

      // Validaciones según tipo de acta
      if (uploadFormData.tipoActa === "REUNION") {
        if (!uploadFormData.proyecto) {
          alert("Para reuniones de seguimiento, debe seleccionar un contrato");
          return;
        }
        if (!uploadFormData.producto) {
          alert("Para reuniones de seguimiento, debe seleccionar un proyecto");
          return;
        }
      } else if (uploadFormData.tipoActa === "COMITE") {
        if (!uploadFormData.comiteId) {
          alert("Debe seleccionar un comité");
          return;
        }
        if (uploadFormData.comiteId === "__NUEVO__" && !uploadNuevoComiteNombre.trim()) {
          alert("Debe ingresar el nombre del nuevo comité");
          return;
        }
      } else if (uploadFormData.tipoActa === "OTRO") {
        if (!uploadFormData.tipoActaCustom) {
          alert("Debe seleccionar una categoría para 'Otro'");
          return;
        }
        if (uploadFormData.tipoActaCustom === "__NUEVO__" && !uploadNuevaCategoriaOtroNombre.trim()) {
          alert("Debe ingresar el nombre de la nueva categoría");
          return;
        }
      } else {
        if (!uploadFormData.proyecto) {
          alert("Debe seleccionar un contrato");
          return;
        }
      }

      if (!uploadFormData.numeroActa) {
        alert("Debe ingresar el número de acta");
        return;
      }

      if (!uploadFormData.pdfFile) {
        alert("Debe seleccionar un archivo PDF");
        return;
      }

      // Verificar si ya existe un acta con el mismo número usando la función de validación (solo si hay proyecto seleccionado)
      if (uploadFormData.proyecto) {
        const validation = await validateActaNumber(
          uploadFormData.numeroActa,
          uploadFormData.proyecto,
          uploadFormData.producto || null
        );

        if (!validation.isValid) {
          alert(validation.message);
          return;
        }
      }

      // Para actas externas no aplicamos validaciones de estado específicas
      // ya que siempre tendrán estado 'externo'

      // Convertir el archivo PDF a base64 y crear URL
      const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      const pdfBase64 = await convertToBase64(uploadFormData.pdfFile);

      // Crear URL del blob para el PDF
      const pdfBlob = new Blob([uploadFormData.pdfFile], {
        type: "application/pdf",
      });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Determinar campos según el tipo de acta y persistir si es nuevo
      let finalTipoActa = uploadFormData.tipoActa;
      let finalTipoProyecto = "";
      let finalTipoProyectos = [];
      let finalProducto = "";
      let finalProductosProyecto = [];
      let finalComiteId = uploadFormData.comiteId;
      let finalTipoActaCustom = uploadFormData.tipoActaCustom;

      if (uploadFormData.tipoActa === "COMITE") {
        let nombreComite = "";
        if (uploadFormData.comiteId === "__NUEVO__") {
          const saved = await saveNewCommitteeIfNeeded(uploadFormData.comiteId, uploadNuevoComiteNombre);
          finalComiteId = saved.id;
          nombreComite = saved.nombre;
        } else {
          const selectedComite = comites.find(
            (c) => c.id === uploadFormData.comiteId
          );
          nombreComite = selectedComite ? selectedComite.nombre : "";
        }
        finalTipoActa = nombreComite ? `COMITE - ${nombreComite}` : "COMITE";
        finalTipoProyecto = nombreComite || "COMITE";
        finalTipoProyectos = [];
      } else if (uploadFormData.tipoActa === "OTRO") {
        if (uploadFormData.tipoActaCustom === "__NUEVO__") {
          const savedCat = await saveNewCategoryOtherIfNeeded(uploadFormData.tipoActaCustom, uploadNuevaCategoriaOtroNombre);
          finalTipoActaCustom = savedCat.nombre;
        }
        finalTipoActa = finalTipoActaCustom || "OTRO";
        finalTipoProyecto = finalTipoActaCustom || "OTRO";
        finalTipoProyectos = [];
      } else {
        const selectedProject = projects.find(
          (p) => p.id === uploadFormData.proyecto
        );
        const projectName = selectedProject
          ? selectedProject.name
          : uploadFormData.proyecto;
        finalTipoProyecto = projectName;
        finalTipoProyectos = uploadFormData.proyecto ? [uploadFormData.proyecto] : [];
        finalProducto = uploadFormData.producto || "";
        finalProductosProyecto = uploadFormData.producto
          ? [uploadFormData.producto]
          : [];
      }

      // Crear el documento del acta externa con la misma estructura que las actas normales
      const actaData = {
        // Información básica del acta
        numeroActa: uploadFormData.numeroActa,
        tipoActa: finalTipoActa,
        tipoActaCustom: finalTipoActaCustom || "",
        comiteId: uploadFormData.tipoActa === "COMITE" ? (finalComiteId || "") : "",
        fecha: uploadFormData.fecha || new Date(),
        horaInicio: new Date(),
        horaFinal: new Date(),
        lugar: uploadFormData.lugar || "Externo",
        objetivo: uploadFormData.objetivo || "Acta externa subida al sistema",
        responsable:
          uploadFormData.responsable || auth.currentUser?.email || "unknown",

        // Información del proyecto (compatible con ambos formatos)
        tipoProyecto: finalTipoProyecto,
        tipoProyectos: finalTipoProyectos,
        producto: finalProducto,
        productoProyecto: finalProducto,
        productosProyecto: finalProductosProyecto,

        // Estado del acta - EXTERNO para actas subidas manualmente
        estadoActa: "externo", // Estado especial para actas externas
        approvalStatus: "externo", // Las actas externas no requieren aprobación

        // Datos del PDF en base64 y URL
        pdfBase64: pdfBase64,
        pdfUrl: pdfUrl,
        fileName: uploadFormData.pdfFile.name,

        // Campos requeridos para compatibilidad con actas normales
        asistentes: [],
        ordenDia: [
          {
            titulo: "Revisión de acta externa",
            descripcion: "Acta externa subida al sistema",
          },
        ],
        compromisos: [],
        observaciones: "Acta externa subida al sistema",

        // Metadatos de creación
        isExternal: true,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || "unknown",
        creatorId: auth.currentUser?.uid || "unknown",
        creatorEmail: auth.currentUser?.email || "unknown",

        // Campos adicionales para funcionalidad completa
        signatures: [],
        signatureRequests: [],
      };

      // Guardar en Firestore
      await addDoc(collection(db, "actas"), actaData);

      // Limpiar el formulario y cerrar el diálogo
      setUploadFormData({
        tipoActa: "REUNION",
        tipoActaCustom: "",
        comiteId: "",
        proyecto: "",
        producto: "",
        numeroActa: "",
        fecha: new Date(),
        objetivo: "",
        responsable: "",
        lugar: "",
        pdfFile: null,
      });
      setUploadActaNumberError("");
      setShowUploadDialog(false);

      alert("Acta externa subida exitosamente");

      // Nota: La recarga de actas se maneja desde el componente padre
    } catch (error) {
      console.error("Error al subir acta externa:", error);
      alert("Error al subir el acta externa. Por favor, intente nuevamente.");
    } finally {
      setUploadLoading(false);
    }
  };

  // Función para preparar los datos para el PDF de previsualización
  const getPreviewData = () => {
    // Agregar los compromisos en curso de actas anteriores a los compromisos actuales
    const uncompletedCommitments = previousCommitments
      .filter((commitment) => commitment.estado === "En curso")
      .map(({ compromiso, responsable, fechaCumplimiento, estado }) => ({
        compromiso,
        responsable,
        fechaCumplimiento,
        estado,
        fromPreviousActa: true,
      }));

    const allCommitments = isEditMode
      ? formData.compromisos
      : [...uncompletedCommitments, ...formData.compromisos];

    // Obtener el nombre del comité si es necesario
    let nombreComite = "";
    if (formData.tipoActa === "COMITE") {
      if (formData.comiteId === "__NUEVO__") {
        nombreComite = (nuevoComiteNombre || "").trim();
      } else if (formData.comiteId) {
        const comiteSeleccionado = comites.find(
          (c) => c.id === formData.comiteId
        );
        if (comiteSeleccionado) {
          nombreComite = comiteSeleccionado.nombre;
        }
      }
    }

    const previewTipoActaCustom =
      formData.tipoActa === "OTRO" && formData.tipoActaCustom === "__NUEVO__"
        ? (nuevaCategoriaOtroNombre || "").trim()
        : formData.tipoActaCustom;

    // Determinar el tipo de proyecto
    let tipoProyecto;
    if (formData.tipoProyectos && formData.tipoProyectos.length > 0) {
      const primerProyectoId = formData.tipoProyectos[0];
      if (primerProyectoId === "OTRO") {
        tipoProyecto =
          formData.tipoProyectoCustom || "Proyecto sin especificar";
      } else {
        const proyecto = projects.find((p) => p.id === primerProyectoId);
        tipoProyecto = proyecto ? proyecto.name : primerProyectoId;
      }
    } else if (formData.tipoProyecto) {
      tipoProyecto =
        formData.tipoProyecto === "OTRO"
          ? formData.tipoProyectoCustom
          : selectedProject
          ? selectedProject.name
          : formData.tipoProyecto;
    } else {
      tipoProyecto = "Proyecto sin especificar";
    }

    return {
      ...formData,
      compromisos: allCommitments,
      tipoActa:
        formData.tipoActa === "OTRO"
          ? previewTipoActaCustom
          : formData.tipoActa === "COMITE" && nombreComite
          ? `COMITE - ${nombreComite}`
          : formData.tipoActa,
      tipoActaCustom: previewTipoActaCustom,
      tipoProyecto: tipoProyecto,
      comiteId: formData.tipoActa === "COMITE" ? (formData.comiteId === "__NUEVO__" ? "" : formData.comiteId) : "",
      approvalStatus: formData.approvalStatus || "por aprobar",
      // Asegurar que las fechas y horas sean válidas para el PDF
      fecha: formData.fecha || new Date(),
      horaInicio: formData.horaInicio || new Date(),
      horaFinal: formData.horaFinal || new Date(),
    };
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box sx={styles.container}>
        <Paper elevation={2} sx={styles.paper}>
          <Box
            sx={{
              m: 3,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0,
                p: 3,
                bgcolor: "rgba(97, 100, 255, 0.05)",
                borderRadius: 2,
                border: "1px solid rgba(97, 100, 255, 0.2)",
                flex: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: "text.primary",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                📄{" "}
                <strong>¿Tienes un acta ya finalizada en formato PDF?</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  lineHeight: 1.6,
                  marginBottom: 2,
                }}
              >
                Si tu reunión ya se realizó y tienes el acta completa en PDF
                (creada fuera de esta plataforma), puedes subirla aquí. El
                sistema la registrará y vinculará con el contrato y proyecto
                correspondiente.
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setShowUploadDialog(true)}
                sx={{
                  bgcolor: "#6164ff",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "0.95rem",
                  p: 3,
                  "&:hover": {
                    bgcolor: "#5a5dff",
                  },
                }}
              >
                <strong>📤 Subir Acta Definitiva (PDF Externo)</strong>
              </Button>
            </Box>

            <Box
              sx={{
                p: 2,
                border: "2px dashed #6164ff",
                borderRadius: 2,
                bgcolor: "#f8f9ff",
                position: "relative",
                flex: 1,
              }}
            >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SmartToyIcon sx={{ color: "#6164ff" }} />
                <Typography
                  variant="h6"
                  sx={{ color: "#6164ff", fontWeight: 600 }}
                >
                  Análisis Inteligente de Reunión
                </Typography>
                <Chip
                  label={
                    currentAiConfig.provider === "minimax"
                      ? `MiniMax: ${currentAiConfig.minimaxModel}`
                      : "Gemini"
                  }
                  size="small"
                  color={
                    currentAiConfig.provider === "minimax"
                      ? "primary"
                      : "default"
                  }
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                />
              </Box>

              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (onNavigateToAiConfig) {
                      onNavigateToAiConfig();
                    } else {
                      setAiParametersOpen(true);
                    }
                  }}
                  startIcon={<SettingsIcon />}
                  sx={{
                    color: "#6164ff",
                    borderColor: "#6164ff",
                    "&:hover": {
                      borderColor: "#5a5dff",
                      bgcolor: "rgba(97, 100, 255, 0.08)",
                    },
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  ⚙️ Configuración IA (Admin)
                </Button>
              )}
            </Box>

            {/* Banner con métricas de la última consulta realizada */}
            {aiMetricsInfo && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #86efac",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ color: "#166534", fontWeight: 700 }}>
                    ⚡ Consumo última consulta ({aiMetricsInfo.model}):
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#15803d" }}>
                    <strong>{aiMetricsInfo.totalTokens.toLocaleString()} tokens</strong> ({aiMetricsInfo.inputTokens.toLocaleString()} entrada / {aiMetricsInfo.outputTokens.toLocaleString()} salida)
                  </Typography>
                  {aiMetricsInfo.costUsd > 0 && (
                    <Typography variant="body2" sx={{ color: "#b45309", fontWeight: 700 }}>
                      | Costo: {aiMetricsInfo.formattedUsd} (~{aiMetricsInfo.formattedCop})
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "#475569" }}>
                    • {aiMetricsInfo.durationMs} ms
                  </Typography>
                </Box>
                {isAdmin && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      if (onNavigateToAiConfig) {
                        onNavigateToAiConfig();
                      } else {
                        setAiParametersOpen(true);
                      }
                    }}
                    sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, color: "#15803d", fontWeight: 600 }}
                  >
                    Ver métricas completas →
                  </Button>
                )}
              </Box>
            )}

            <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
              <strong>
                Sube un archivo de transcripción (.txt, .vtt, .docx) y una lista
                de asistencia (.csv) de tu reunión de Teams.
              </strong>
              <br />
              La IA analizará el contenido y completará automáticamente los
              campos del formulario, incluyendo los asistentes con sus correos
              electrónicos.
            </Typography>

            <Typography
              variant="body2"
              sx={{ mb: 2, color: "#d32f2f", fontWeight: "bold" }}
            >
              ⚠️ Si tiene documentos para análisis de IA, debe cargarlos primero
              antes de llenar el formulario manualmente, ya que la IA
              reemplazará la información existente.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={aiLoading || csvLoading}
                sx={{
                  bgcolor: "#6164ff",
                  "&:hover": { bgcolor: "#5a5dff" },
                  minWidth: "200px",
                }}
              >
                Subir Transcripción
                <input
                  type="file"
                  accept=".txt,.vtt,.docx,text/plain,text/vtt,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileUpload}
                  hidden
                  disabled={aiLoading || csvLoading}
                />
              </Button>

              <Button
                variant="contained"
                component="label"
                startIcon={
                  csvLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                disabled={aiLoading || csvLoading}
                sx={{
                  bgcolor: "#4caf50",
                  "&:hover": { bgcolor: "#45a049" },
                  minWidth: "200px",
                }}
              >
                {csvLoading ? "Procesando..." : "Subir Lista de asistencia CSV"}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvUpload}
                  hidden
                  disabled={aiLoading || csvLoading}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearForm}
                sx={{
                  color: "#6164ff",
                  borderColor: "#6164ff",
                  "&:hover": {
                    borderColor: "#5a5dff",
                    bgcolor: "rgba(97, 100, 255, 0.04)",
                  },
                }}
              >
                Limpiar Formulario
              </Button>
            </Box>

            {/* Indicadores de archivos cargados */}
            {(transcriptionFile || csvFile) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  {transcriptionFile && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        bgcolor: "rgba(97, 100, 255, 0.1)",
                        borderRadius: 1,
                        border: "1px solid rgba(97, 100, 255, 0.3)",
                      }}
                    >
                      <UploadFileIcon sx={{ color: "#6164ff", fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "#6164ff", flex: 1 }}
                      >
                        📄 {transcriptionFile.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleRemoveTranscriptionFile}
                        sx={{ color: "#6164ff" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {csvFile && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                        borderRadius: 1,
                        border: "1px solid rgba(76, 175, 80, 0.3)",
                      }}
                    >
                      <UploadFileIcon sx={{ color: "#4caf50", fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "#4caf50", flex: 1 }}
                      >
                        📊 {csvFile.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleRemoveCsvFile}
                        sx={{ color: "#4caf50" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Botón para comenzar el procesamiento */}
                {(transcriptionFile || csvFile) &&
                  !aiLoading &&
                  !csvLoading && (
                    <Button
                      variant="contained"
                      onClick={handleStartProcessing}
                      startIcon={<SmartToyIcon />}
                      sx={{
                        bgcolor: "#ff9800",
                        "&:hover": { bgcolor: "#f57c00" },
                        fontWeight: 600,
                      }}
                    >
                      Comenzar Análisis
                    </Button>
                  )}
              </Box>
            )}

            {(aiLoading || csvLoading) && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      bgcolor: csvLoading ? "#4caf50" : "#6164ff",
                    },
                    bgcolor: csvLoading
                      ? "rgba(76, 175, 80, 0.1)"
                      : "rgba(97, 100, 255, 0.1)",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#666", mt: 1, display: "block" }}
                >
                  {csvLoading
                    ? "Procesando lista de asistencia..."
                    : "Procesando transcripción con IA..."}
                </Typography>
              </Box>
            )}
          </Box>
          </Box>

          {/* Snackbar para mostrar mensajes de IA */}
          <Snackbar
            open={showAiAlert}
            autoHideDuration={6000}
            onClose={handleCloseAlert}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseAlert}
              severity={aiError ? "error" : "success"}
              sx={{ width: "100%" }}
            >
              {aiError || aiSuccess}
            </Alert>
          </Snackbar>

          {/* Barra de progreso */}
          <Box sx={styles.progressContainer}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={styles.progressBar}
            />
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              {progressPercentage}% completado
            </Typography>
          </Box>

          {formData.tipoActa &&
            (editingActa ||
              (formData.tipoProyectos.length > 0 &&
                formData.productosProyecto.length > 0) ||
              (formData.tipoActa === "COMITE" && formData.comiteId) ||
              (formData.tipoActa === "OTRO" && formData.tipoActaCustom)) && (
            <Box sx={{ mx: 3, mt: 1, mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                {editingNumeroActa ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2">Acta N°:</Typography>
                      <TextField
                        size="small"
                        value={tempNumeroActa}
                        onChange={(e) => setTempNumeroActa(e.target.value)}
                        error={!!numeroActaError}
                        sx={{ width: "120px" }}
                        inputProps={{
                          style: { textAlign: "center" },
                          type: "text",
                          pattern: "[-]?[0-9]*.?[0-9]*",
                          title:
                            "Permite números enteros, decimales y negativos",
                        }}
                        placeholder="ej: -1, 0.5, 1.25"
                      />
                      <IconButton
                        size="small"
                        onClick={handleGuardarNumeroActa}
                        color="primary"
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelarNumeroActa}
                        color="secondary"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {numeroActaError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {numeroActaError}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          border: "1px solid #ccc",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: "#f9f9f9",
                          color: "#333",
                        }}
                      >
                        {`Acta N°: ${formData.numeroActa}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleEditarNumeroActa}
                        sx={{ ml: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {(formData.tipoProyectos.length > 0 ||
                      formData.tipoActa === "REUNION" ||
                      formData.tipoActa === "COMITE" ||
                      formData.tipoActa === "OTRO") && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                          }}
                        >
                          Estado:
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            name="estadoActa"
                            value={formData.estadoActa}
                            onChange={handleEstadoActaChange}
                            displayEmpty
                            sx={{ fontSize: "0.8rem", height: "32px" }}
                          >
                            <MenuItem
                              value="sin_iniciar"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              Sin iniciar
                            </MenuItem>
                            <MenuItem value="en_curso" sx={{ fontSize: "0.8rem" }}>
                              En curso
                            </MenuItem>
                            <MenuItem
                              value="finalizado"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              Finalizado
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* Contenido del formulario */}
          <Box sx={styles.formContent}>
            {/* Sección: Datos de la Reunión */}
            <Box sx={styles.sectionContainer}>
              <Typography variant="h6" gutterBottom>
                Datos de la reunión
              </Typography>

              <Grid container spacing={2}>
                {/* Tipo de acta completamente horizontal */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(105, 108, 255, 0.2)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(105, 108, 255, 0.4)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#696cff",
                        },
                      },
                    }}
                  >
                    <InfoIconWithTooltip y="" title="" />
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>
                        1. Tipo de acta <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <Select
                        name="tipoActa"
                        value={formData.tipoActa}
                        onChange={handleChange}
                        label="Tipo de acta"
                      >
                        <MenuItem value="REUNION">
                          Reunión del contrato o convenio
                        </MenuItem>
                        <MenuItem value="COMITE">Comité</MenuItem>
                        <MenuItem value="OTRO">Otro</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Selector de Comité - Solo visible cuando el tipo de acta es COMITE */}
                  {formData.tipoActa === "COMITE" && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>
                          Comité <span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <Select
                          name="comiteId"
                          value={formData.comiteId}
                          onChange={(e) => {
                            handleChange(e);
                            if (e.target.value === "__NUEVO__") {
                              if (!vinculaContratoProyecto && !editingActa) {
                                setFormData((prev) => ({ ...prev, numeroActa: "1" }));
                              }
                            }
                          }}
                          label="Comité"
                          disabled={loadingComites}
                        >
                          {loadingComites ? (
                            <MenuItem disabled>Cargando comités...</MenuItem>
                          ) : comites.length > 0 ? (
                            comites.map((comite) => (
                              <MenuItem key={comite.id} value={comite.id}>
                                {comite.nombre}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>
                              No hay comités disponibles
                            </MenuItem>
                          )}
                          <Divider sx={{ my: 0.5 }} />
                          <MenuItem
                            value="__NUEVO__"
                            sx={{
                              fontStyle: "italic",
                              color: "#1976d2",
                              fontWeight: 600,
                            }}
                          >
                            ➕ Otro (Ingresar nuevo comité manualmente)...
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {formData.comiteId === "__NUEVO__" && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Nombre del nuevo comité *"
                          value={nuevoComiteNombre}
                          onChange={(e) => {
                            setNuevoComiteNombre(e.target.value);
                            if (!vinculaContratoProyecto && !editingActa) {
                              setFormData((prev) => ({ ...prev, numeroActa: "1" }));
                            }
                          }}
                          placeholder="Ej: Comité de Ética e Investigación"
                          sx={{ mt: 1.5 }}
                          helperText="Se guardará automáticamente al crear el acta y quedará disponible para futuras actas"
                        />
                      )}
                      
                      {/* Checkbox para vincular contrato/proyecto en Comité */}
                      <Box sx={{ mt: 1, ml: 1, display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={vinculaContratoProyecto}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setVinculaContratoProyecto(checked);
                                
                                if (!checked) {
                                  // Si se desmarca, limpiar proyectos seleccionados y actualizar número
                                  setFormData(prev => ({
                                    ...prev,
                                    tipoProyectos: [],
                                    productosProyecto: [],
                                    tipoProyecto: "", // Limpiar compatibilidad legacy
                                    productoProyecto: ""
                                  }));
                                  
                                  // Si hay comité seleccionado, buscar su consecutivo
                                  if (formData.comiteId) {
                                    fetchNextActaNumberForCommittee(formData.comiteId);
                                  }
                                } else {
                                  // Si se marca, el número se actualizará cuando seleccione un proyecto
                                  // O podemos setearlo al nextActaNumber global por defecto
                                  if (!editingActa) {
                                    setFormData(prev => ({
                                      ...prev,
                                      numeroActa: nextActaNumber.toString()
                                    }));
                                  }
                                }
                              }}
                              name="vinculaContratoProyecto"
                              color="primary"
                            />
                          }
                          label="Vincular con Contrato y Proyecto"
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Campo personalizado para tipo de acta cuando se selecciona OTRO */}
                  {formData.tipoActa === "OTRO" && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>
                          Categoría Otro <span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <Select
                          name="tipoActaCustom"
                          value={formData.tipoActaCustom}
                          onChange={(e) => {
                            handleChange(e);
                            if (e.target.value === "__NUEVO__") {
                              if (!vinculaContratoProyecto && !editingActa) {
                                setFormData((prev) => ({ ...prev, numeroActa: "1" }));
                              }
                            }
                          }}
                          label="Categoría Otro"
                          disabled={loadingCategoriasOtro}
                        >
                          {loadingCategoriasOtro ? (
                            <MenuItem disabled>
                              Cargando categorías...
                            </MenuItem>
                          ) : categoriasOtro.length > 0 ? (
                            categoriasOtro.map((categoria) => (
                              <MenuItem key={categoria.id} value={categoria.nombre}>
                                {categoria.nombre}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>
                              No hay categorías disponibles
                            </MenuItem>
                          )}
                          <Divider sx={{ my: 0.5 }} />
                          <MenuItem
                            value="__NUEVO__"
                            sx={{
                              fontStyle: "italic",
                              color: "#1976d2",
                              fontWeight: 600,
                            }}
                          >
                            ➕ Otra categoría (Ingresar manualmente)...
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {formData.tipoActaCustom === "__NUEVO__" && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Nombre de la nueva categoría *"
                          value={nuevaCategoriaOtroNombre}
                          onChange={(e) => {
                            setNuevaCategoriaOtroNombre(e.target.value);
                            if (!vinculaContratoProyecto && !editingActa) {
                              setFormData((prev) => ({ ...prev, numeroActa: "1" }));
                            }
                          }}
                          placeholder="Ej: Taller de socialización"
                          sx={{ mt: 1.5 }}
                          helperText="Se guardará automáticamente al crear el acta y quedará disponible para futuras actas"
                        />
                      )}

                      {/* Checkbox para vincular contrato/proyecto en OTRO */}
                      <Box sx={{ mt: 1, ml: 1, display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={vinculaContratoProyecto}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setVinculaContratoProyecto(checked);
                                
                                if (!checked) {
                                  // Si se desmarca, limpiar proyectos seleccionados y actualizar número
                                  setFormData(prev => ({
                                    ...prev,
                                    tipoProyectos: [],
                                    productosProyecto: [],
                                    tipoProyecto: "", // Limpiar compatibilidad legacy
                                    productoProyecto: ""
                                  }));
                                  
                                  // Si hay categoría seleccionada, buscar su consecutivo
                                  if (formData.tipoActaCustom) {
                                    fetchNextActaNumberForCategoryOther(formData.tipoActaCustom);
                                  }
                                } else {
                                  // Si se marca, el número se actualizará cuando seleccione un proyecto
                                  // O podemos setearlo al nextActaNumber global por defecto
                                  if (!editingActa) {
                                    setFormData(prev => ({
                                      ...prev,
                                      numeroActa: nextActaNumber.toString()
                                    }));
                                  }
                                }
                              }}
                              name="vinculaContratoProyecto"
                              color="primary"
                            />
                          }
                          label="Vincular con Contrato y Proyecto"
                        />
                      </Box>
                    </Box>
                  )}
                </Grid>

                {/* Sección Principal - Contratos y Proyectos en Horizontal */}
                {formData.tipoActa === "REUNION" || vinculaContratoProyecto ? (
                  <Grid item xs={12}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      mb: 3,
                      backgroundColor: "#fafafa",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 3,
                        alignItems: "flex-start",
                        width: "100%",
                        overflow: "hidden"
                      }}
                    >
                      {/* Columna Izquierda - Contratos */}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 2, color: "#1976d2", fontWeight: 600 }}
                        >
                          🏢 Contratos
                        </Typography>

                        {/* Contratos Seleccionados */}
                        {formData.tipoProyectos.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, color: "#1976d2", fontWeight: 600 }}
                            >
                              ✅ Seleccionados ({formData.tipoProyectos.length})
                            </Typography>
                            <Box
                              sx={{ 
                                display: "flex", 
                                flexWrap: "wrap", 
                                gap: 1,
                                maxWidth: "100%",
                                overflow: "hidden"
                              }}
                            >
                              {Array.isArray(formData.tipoProyectos) &&
                                formData.tipoProyectos.map(
                                  (proyecto, index) => (
                                    <Chip
                                      key={index}
                                      label={
                                        proyecto === "OTRO"
                                          ? formData.tipoProyectoCustom
                                          : projects.find(
                                              (p) => p.id === proyecto
                                            )?.name || proyecto
                                      }
                                      onDelete={async () => {
                                        const proyectoEliminado =
                                          formData.tipoProyectos[index];
                                        const nuevosProyectos =
                                          formData.tipoProyectos.filter(
                                            (_, i) => i !== index
                                          );

                                        const nuevosProductos =
                                          formData.productosProyecto.filter(
                                            (producto) => {
                                              if (
                                                proyectoEliminado === "OTRO"
                                              ) {
                                                return true;
                                              }

                                              const proyecto = projects.find(
                                                (p) =>
                                                  p.id === proyectoEliminado
                                              );
                                              if (
                                                !proyecto ||
                                                !proyecto.products
                                              ) {
                                                return true;
                                              }

                                              const perteneceAlProyecto =
                                                proyecto.products.some(
                                                  (p) =>
                                                    p === producto ||
                                                    (typeof p === "object" &&
                                                      p.id === producto)
                                                );
                                              return !perteneceAlProyecto;
                                            }
                                          );

                                        setFormData((prev) => ({
                                          ...prev,
                                          tipoProyectos: nuevosProyectos,
                                          productosProyecto: nuevosProductos,
                                        }));

                                        // Buscar compromisos de actas anteriores para la nueva combinación
                                        setTimeout(() => {
                                          fetchPreviousCommitmentsForContractProjects(
                                            nuevosProyectos,
                                            nuevosProductos
                                          );
                                        }, 100);

                                        // Resto de tu lógica...
                                      }}
                                      color="primary"
                                      variant="filled"
                                      size="small"
                                      sx={{
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                        fontWeight: 600,
                                        maxWidth: "200px",
                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "150px"
                                        },
                                        "& .MuiChip-deleteIcon": {
                                          color: "white",
                                          "&:hover": {
                                            color: "#ffcdd2",
                                          },
                                        },
                                      }}
                                    />
                                  )
                                )}
                            </Box>
                          </Box>
                        )}

                        {/* Selector de Contratos */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1,
                            alignItems: { xs: "stretch", sm: "flex-end" },
                          }}
                        >
                          <FormControl
                            sx={{ flex: 1, minWidth: 0 }}
                            variant="outlined"
                            size="small"
                          >
                            <InputLabel>
                              2. Seleccionar contrato
                              {formData.tipoActa !== "COMITE" ? (
                                <span style={{ color: "red" }}> *</span>
                              ) : (
                                ""
                              )}
                            </InputLabel>
                            <Select
                              name="proyectoSeleccionado"
                              value={proyectoSeleccionado}
                              onChange={(e) =>
                                setProyectoSeleccionado(e.target.value)
                              }
                              label={`Seleccionar contrato${
                                formData.tipoActa !== "COMITE" ? " *" : ""
                              }`}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                    width: 'auto',
                                    minWidth: 250
                                  }
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>Seleccione un contrato</em>
                              </MenuItem>
                              {projects.length > 0 ? (
                                []
                                  .concat(
                                    projects
                                      .filter(
                                        (project) =>
                                          !formData.tipoProyectos.includes(
                                            project.id
                                          )
                                      )
                                      .map((project) => (
                                        <MenuItem
                                          key={project.id}
                                          value={project.id}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              width: "100%",
                                              overflow: "hidden"
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor: "#1976d2",
                                                flexShrink: 0
                                              }}
                                            />
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                flex: 1
                                              }}
                                            >
                                              {project.name || "Contrato sin nombre"}
                                            </Typography>
                                          </Box>
                                        </MenuItem>
                                      )),
                                    !formData.tipoProyectos.includes("OTRO") ? (
                                      <MenuItem key="otro" value="OTRO">
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#ff9800",
                                            }}
                                          />
                                          OTRO (Contrato personalizado)
                                        </Box>
                                      </MenuItem>
                                    ) : null
                                  )
                                  .filter(Boolean)
                              ) : (
                                <MenuItem disabled>
                                  No hay contratos disponibles
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>

                          <Button
                            variant="contained"
                            color="primary"
                            disabled={!proyectoSeleccionado}
                            size="small"
                            sx={{
                              minWidth: { xs: "100%", sm: "100px" },
                              height: "40px",
                              fontWeight: 600,
                              flexShrink: 0
                            }}
                            onClick={async () => {
                              if (proyectoSeleccionado) {
                                // Tu lógica de agregar contrato existente aquí
                                const nuevosProyectos = [
                                  ...formData.tipoProyectos,
                                  proyectoSeleccionado,
                                ];

                                const historyValidation =
                                  await validateContractHistory(
                                    [proyectoSeleccionado],
                                    []
                                  );
                                if (historyValidation.hasHistory) {
                                  const confirmAdd = window.confirm(
                                    `${historyValidation.message}\n\n¿Desea continuar agregando este contrato/proyecto?`
                                  );
                                  if (!confirmAdd) {
                                    return;
                                  }
                                }

                                // Resto de tu lógica...
                                setFormData((prev) => ({
                                  ...prev,
                                  tipoProyectos: nuevosProyectos,
                                }));
                                setProyectoSeleccionado("");
                                
                                // Buscar compromisos de actas anteriores para la nueva combinación
                                setTimeout(() => {
                                  fetchPreviousCommitmentsForContractProjects(
                                    nuevosProyectos,
                                    formData.productosProyecto
                                  );
                                }, 100);
                              }
                            }}
                          >
                            ➕ Agregar
                          </Button>
                        </Box>

                        {/* Campo personalizado para contrato */}
                        {formData.tipoProyectos.includes("OTRO") && (
                          <Box sx={{ mt: 2 }}>
                            <TextField
                              fullWidth
                              label="Nombre del Contrato Personalizado"
                              name="tipoProyectoCustom"
                              value={formData.tipoProyectoCustom}
                              onChange={handleChange}
                              variant="outlined"
                              size="small"
                              placeholder="Ingrese el nombre del contrato"
                            />
                          </Box>
                        )}
                      </Box>

                      {/* Separador Visual */}
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                      {/* Columna Derecha - Proyectos */}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 2, color: "#f57c00", fontWeight: 600 }}
                        >
                          🎯 Proyectos
                        </Typography>

                        {formData.tipoProyectos.length > 0 ? (
                          <>
                            {/* Información del responsable */}
                            {projectManager && (
                              <Box
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  backgroundColor: "#f0f7ff",
                                  borderRadius: 1,
                                  border: "1px solid #bbdefb",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#1976d2", fontWeight: 600 }}
                                >
                                  👤 Aprobador del proyecto
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#1976d2",
                                    fontWeight: 500,
                                    mt: 0.5,
                                  }}
                                >
                                  {projectManager}
                                </Typography>
                              </Box>
                            )}

                            {/* Productos Seleccionados */}
                            {formData.productosProyecto.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 1,
                                    color: "#f57c00",
                                    fontWeight: 600,
                                  }}
                                >
                                  ✅ Seleccionados (
                                  {formData.productosProyecto.length})
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    maxWidth: "100%",
                                    overflow: "hidden"
                                  }}
                                >
                                  {Array.isArray(formData.productosProyecto) &&
                                    formData.productosProyecto.map(
                                      (producto, index) => (
                                        <Chip
                                          key={index}
                                          label={producto}
                                          onDelete={async () => {
                                            // Tu lógica de eliminación de productos existente
                                            const nuevosProductos =
                                              formData.productosProyecto.filter(
                                                (_, i) => i !== index
                                              );
                                            setFormData((prev) => ({
                                              ...prev,
                                              productosProyecto:
                                                nuevosProductos,
                                            }));
                                            
                                            // Buscar compromisos de actas anteriores para la nueva combinación
                                            setTimeout(() => {
                                              fetchPreviousCommitmentsForContractProjects(
                                                formData.tipoProyectos,
                                                nuevosProductos
                                              );
                                            }, 100);
                                            
                                            // Resto de tu lógica...
                                          }}
                                          color="warning"
                                          variant="filled"
                                          size="small"
                                          sx={{
                                            backgroundColor: "#f57c00",
                                            color: "white",
                                            fontWeight: 600,
                                            maxWidth: "200px",
                                             "& .MuiChip-label": {
                                               overflow: "hidden",
                                               textOverflow: "ellipsis",
                                               whiteSpace: "nowrap",
                                               maxWidth: "150px"
                                             },
                                            "& .MuiChip-deleteIcon": {
                                              color: "white",
                                              "&:hover": {
                                                color: "#ffcdd2",
                                              },
                                            },
                                          }}
                                        />
                                      )
                                    )}
                                </Box>
                              </Box>
                            )}

                            {/* Selector de Proyectos */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 1,
                                alignItems: { xs: "stretch", sm: "flex-end" },
                              }}
                            >
                              <FormControl
                                sx={{ flex: 1, minWidth: 0 }}
                                variant="outlined"
                                size="small"
                              >
                                <InputLabel>
                                  3. Seleccionar proyecto
                                  {formData.tipoActa === "REUNION" ? (
                                    <span style={{ color: "red" }}> *</span>
                                  ) : (
                                    ""
                                  )}
                                </InputLabel>
                                <Select
                                  name="productoSeleccionado"
                                  value={productoSeleccionado}
                                  onChange={(e) =>
                                    setProductoSeleccionado(e.target.value)
                                  }
                                  label={`Seleccionar proyecto${
                                    formData.tipoActa === "REUNION" ? " *" : ""
                                  }`}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                        width: 'auto',
                                        minWidth: 250
                                      }
                                    }
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Seleccione un proyecto</em>
                                  </MenuItem>
                                  <MenuItem value="TRANSVERSAL">
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: "#4caf50",
                                        }}
                                      />
                                      TRANSVERSAL
                                    </Box>
                                  </MenuItem>
                                  {formData.tipoProyectos.length > 0 &&
                                    formData.tipoProyectos[0] !== "OTRO" &&
                                    projects
                                      .filter((project) =>
                                        formData.tipoProyectos.includes(
                                          project.id
                                        )
                                      )
                                      .flatMap(
                                        (project) => project.products || []
                                      )
                                      .filter(
                                        (product) =>
                                          !formData.productosProyecto.includes(
                                            product
                                          )
                                      )
                                      .map((product, index) => (
                                        <MenuItem key={index} value={product}>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              width: "100%",
                                              overflow: "hidden"
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor: "#f57c00",
                                                flexShrink: 0
                                              }}
                                            />
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                flex: 1
                                              }}
                                            >
                                              {product}
                                            </Typography>
                                          </Box>
                                        </MenuItem>
                                      ))}
                                </Select>
                              </FormControl>

                              <Button
                                variant="contained"
                                color="warning"
                                disabled={!productoSeleccionado}
                                size="small"
                                sx={{
                                  minWidth: { xs: "100%", sm: "100px" },
                                  height: "40px",
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                                onClick={async () => {
                                  if (productoSeleccionado) {
                                    // Tu lógica de agregar producto existente aquí
                                    if (
                                      formData.tipoProyectos &&
                                      formData.tipoProyectos.length > 0
                                    ) {
                                      const historyValidation =
                                        await validateContractHistory(
                                          formData.tipoProyectos,
                                          [productoSeleccionado]
                                        );
                                      if (historyValidation.hasHistory) {
                                        const confirmAdd = window.confirm(
                                          `${historyValidation.message}\n\n¿Desea continuar agregando este producto?`
                                        );
                                        if (!confirmAdd) {
                                          return;
                                        }
                                      }
                                    }

                                    const nuevosProductos = [
                                      ...formData.productosProyecto,
                                      productoSeleccionado,
                                    ];
                                    setFormData((prev) => ({
                                      ...prev,
                                      productosProyecto: nuevosProductos,
                                    }));

                                    // Resto de tu lógica...
                                    setProductoSeleccionado("");
                                    
                                    // Buscar compromisos de actas anteriores para la nueva combinación
                                    setTimeout(() => {
                                      fetchPreviousCommitmentsForContractProjects(
                                        formData.tipoProyectos,
                                        nuevosProductos
                                      );
                                    }, 100);
                                  }
                                }}
                              >
                                ➕ Agregar
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <Box
                            sx={{
                              p: 2,
                              textAlign: "center",
                              backgroundColor: "#fff8e1",
                              borderRadius: 1,
                              border: "1px dashed #ffb74d",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#f57c00", fontStyle: "italic" }}
                            >
                              Primero seleccione al menos un contrato
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                ) : null}

                {/* Campos restantes en disposición horizontal */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoIconWithTooltip
                      y="0"
                      title="Ingrese el nombre completo de la persona encargada de la reunión o actividad registrada en el acta."
                    />
                    <TextField
                      fullWidth
                      label="4. Responsable"
                      name="responsable"
                      placeholder="Nombre del responsable"
                      value={formData.responsable}
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoIconWithTooltip
                      y="0"
                      title="Indique el lugar donde se realizará la reunión, puede ser una ubicación física o virtual (Teams, Zoom)"
                    />
                    <TextField
                      fullWidth
                      label="5. Lugar"
                      name="lugar"
                      placeholder="Sala de Juntas 3 - Piso 4"
                      value={formData.lugar}
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Grid>

                {/* Checkbox Sin firma */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoIconWithTooltip
                      y="0"
                      title="Marque esta opción si el acta no requiere firma digital. Al activarla, se deshabilitará la sección de firma."
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.sinFirma}
                          onChange={(e) => setFormData(prev => ({ ...prev, sinFirma: e.target.checked }))}
                          name="sinFirma"
                          color="primary"
                        />
                      }
                      label="Sin firmas (Al activar esta opción, se deshabilitará el requerimiento de firmas digitales para los asistentes y todas las funciones relacionadas con firmas)"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }
                      }}
                    />
                  </Box>
                </Grid>

                {/* Sección de fecha y horas en horizontal */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoIconWithTooltip title="Registrar la fecha de la reunión, indicando la hora de inicio y finalización, especificando si es a.m. o p.m." />
                    <DatePicker
                      label="6. Fecha"
                      value={formData.fecha}
                      onChange={(newValue) =>
                        setFormData((prev) => ({ ...prev, fecha: newValue }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                      inputFormat="dd/MM/yyyy"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TimePicker
                    label="Inicio"
                    value={formData.horaInicio}
                    onChange={(newValue) =>
                      setFormData((prev) => ({ ...prev, horaInicio: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                    ampm={true}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TimePicker
                    label="Fin"
                    value={formData.horaFinal}
                    onChange={(newValue) =>
                      setFormData((prev) => ({ ...prev, horaFinal: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                    ampm={true}
                  />
                </Grid>

                {/* Sección de imágenes */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoIconWithTooltip
                      y="0"
                      title="Adjunte una imagen que se mostrará en el PDF del acta (opcional). Formatos soportados: JPG, PNG, GIF"
                    />
                    <Box sx={{ width: "100%" }}>
                      <input
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        id="imagen-upload"
                        type="file"
                        onChange={handleImageUpload}
                      />
                      <label htmlFor="imagen-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                          }}
                        >
                          {formData.imagenesAdjuntas.length > 0
                            ? `${formData.imagenesAdjuntas.length} imagen(es) cargada(s) - Agregar más`
                            : "Seleccionar imágenes (opcional)"}
                        </Button>
                      </label>
                      {formData.imagenesAdjuntas.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: "bold" }}
                          >
                            Imágenes adjuntas:
                          </Typography>
                          {formData.imagenesAdjuntas.map((imagen) => (
                            <Box
                              key={imagen.id}
                              sx={{
                                mb: 2,
                                p: 2,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <TextField
                                  size="small"
                                  label="Nombre de la imagen"
                                  value={imagen.nombre}
                                  onChange={(e) =>
                                    handleImageNameChange(
                                      imagen.id,
                                      e.target.value
                                    )
                                  }
                                  sx={{ flexGrow: 1 }}
                                />
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleImageDelete(imagen.id)}
                                >
                                  Eliminar
                                </Button>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={imagen.imagen}
                                  alt={imagen.nombre}
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "150px",
                                    objectFit: "contain",
                                    border: "1px solid #ddd",
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                border: "1px solid #d1d5db",
                p: 2,
                borderRadius: 1,
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "rgba(105, 108, 255, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(105, 108, 255, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#696cff",
                  },
                },
              }}
            >
              {/* Sección: Objetivo de la Reunión */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: ".4rem" }}
                >
                  <InfoIconWithTooltip
                    y="-25"
                    title="Indicar el propósito de la reunión, comenzando con un verbo en infinitivo (por ejemplo: analizar, definir, revisar)."
                  />
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#2b2b2b", fontWeight: "600" }}
                  >
                    7. Objetivo de la reunión
                  </Typography>
                </Box>

                <ObjetivoRichEditor
                  value={formData.objetivo}
                  onChange={(newVal) => {
                    setFormData((prev) => ({
                      ...prev,
                      objetivo: newVal,
                    }));
                  }}
                  placeholder="Revisar el avance del desarrollo del módulo de gestión documental y definir próximos pasos para la implementación del sistema."
                />
              </Box>

              {/* Sección: Orden del día */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: ".4rem" }}
                >
                  <InfoIconWithTooltip
                    y="-25"
                    title="Registrar los temas tratados y su desarrollo en orden cronológico."
                  />
                  <Typography variant="h6" gutterBottom>
                    8. Orden del día
                  </Typography>
                </Box>

                {formData.ordenDia.length > 0 ? (
                  formData.ordenDia.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        border: "1px solid #d1d5db",
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          bgcolor: "#e3f2fd",
                          color: "#696cff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {editingOrdenIndex === index ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="Título"
                              name="titulo"
                              value={tempOrdenItem.titulo}
                              onChange={handleTempOrdenItemChange}
                              variant="outlined"
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Descripción"
                              name="descripcion"
                              value={tempOrdenItem.descripcion}
                              onChange={handleTempOrdenItemChange}
                              variant="outlined"
                              size="small"
                              multiline
                              rows={2}
                            />
                          </Box>
                        ) : (
                          <>
                            <Typography
                              variant="subtitle1"
                              sx={{ color: "#333", fontWeight: "bold" }}
                            >
                              {item.titulo}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#999" }}>
                              {item.descripcion}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box>
                        {editingOrdenIndex === index ? (
                          <>
                            <IconButton
                              size="small"
                              sx={{ color: "#4caf50" }}
                              onClick={handleGuardarOrdenItem}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: "#f44336" }}
                              onClick={handleCancelarOrdenItem}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              sx={{ color: "#696cff" }}
                              onClick={() => handleEditarOrdenItem(index)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              sx={{ color: "tomato" }}
                              size="small"
                              onClick={() => handleEliminarOrdenItem(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "#f8f8f8",
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    {previousOrdenDia.length > 0 ? (
                      <>
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          sx={styles.previousOrdenDiaTitle}
                        >
                          Puntos del orden del día del acta anterior{" "}
                          {previousOrdenDia[0]?.fromActaNumber
                            ? `(Acta #${previousOrdenDia[0].fromActaNumber})`
                            : ""}
                          :
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mb: 2, color: "#666", fontStyle: "italic" }}
                        >
                          Estos puntos provienen del acta anterior con la misma
                          combinación de contratos y proyectos (solo para
                          referencia).
                        </Typography>
                        {previousOrdenDia.map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              ...styles.previousOrdenDia,
                              display: "flex",
                              alignItems: "flex-start",
                              textAlign: "left",
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                bgcolor: "#e3f2fd",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 2,
                                fontSize: "0.8rem",
                                color: "#696cff",
                              }}
                            >
                              {index + 1}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "#121212" }}
                              >
                                {item.titulo}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.descripcion}
                              </Typography>
                              {item.fromActaNumber && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#666", fontStyle: "italic" }}
                                >
                                  Origen: Acta #{item.fromActaNumber}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 2 }}
                        >
                          Agregue los temas a tratar en la reunión actual
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1" color="textSecondary">
                          No hay puntos en el orden del día
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 1 }}
                        >
                          Agregue los temas a tratar en la reunión
                        </Typography>
                      </>
                    )}
                  </Box>
                )}

                {/* Formulario para agregar nuevo punto */}
                <Box sx={{ mt: 3, borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Título"
                        name="titulo"
                        value={nuevoOrdenItem.titulo}
                        onChange={handleOrdenItemChange}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Descripción"
                        name="descripcion"
                        value={nuevoOrdenItem.descripcion}
                        onChange={handleOrdenItemChange}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAgregarOrdenItem}
                        fullWidth
                        sx={{ height: "100%", bgcolor: "#6164ff" }}
                      >
                        + Agregar
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>

            {/* Sección: Compromisos Establecidos */}
            <Box
              sx={{
                mb: 4,
                border: "1px solid #d1d5db",
                p: 2,
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "rgba(105, 108, 255, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(105, 108, 255, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#696cff",
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: ".4rem" }}>
                <InfoIconWithTooltip
                  y="-20"
                  title="Registrar los compromisos acordados, incluyendo la actividad, responsable y fecha de cumplimiento."
                />
                <Typography variant="h6" gutterBottom>
                  9. Compromisos establecidos
                </Typography>
              </Box>

              {/* Compromisos pendientes de actas anteriores */}
              {loadingCommitments ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : window.missingIndexUrl ? (
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: "#fff3cd",
                    border: "1px solid #ffeeba",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#856404" }}
                  >
                    Se requiere configuración adicional
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Para poder buscar compromisos de actas anteriores, es
                    necesario crear un índice en Firebase.
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    href={window.missingIndexUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mb: 2 }}
                  >
                    Crear Índice en Firebase
                  </Button>
                </Paper>
              ) : previousCommitments.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#0369a1" }}
                  >
                    Compromisos del acta anterior{" "}
                    {previousCommitments[0]?.fromActaNumber
                      ? `(Acta #${previousCommitments[0].fromActaNumber})`
                      : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "#666", fontStyle: "italic" }}
                  >
                    Estos compromisos provienen del acta anterior con la misma
                    combinación de contratos y proyectos.
                  </Typography>
                  {previousCommitments.map((compromiso, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "#e7e7ff",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #d1d5db",
                      }}
                    >
                      <FormControl
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 120, mr: 2 }}
                      >
                        <Select
                          value={compromiso.estado || "En curso"}
                          onChange={(e) =>
                            handlePreviousCommitmentChange(
                              index,
                              e.target.value
                            )
                          }
                          size="small"
                          sx={{
                            border: "1px solid #eee",
                          }}
                        >
                          <MenuItem value="En curso">En curso</MenuItem>
                          <MenuItem value="Terminado">Terminado</MenuItem>
                        </Select>
                      </FormControl>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#121212",
                            fontWeight: 600,
                            fontSize: ".9rem",
                          }}
                        >
                          {compromiso.compromiso}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2.5, mt: 1 }}>
                          <Typography variant="body2">
                            <strong style={{ color: "#606040" }}>
                              Responsable:
                            </strong>{" "}
                            {compromiso.responsable}
                          </Typography>
                          <Typography variant="body2">
                            <strong style={{ color: "#606040" }}>Fecha:</strong>{" "}
                            {compromiso.fechaCumplimiento?.toLocaleDateString()}
                          </Typography>
                          {compromiso.fromActaNumber && (
                            <Typography variant="body2">
                              <strong style={{ color: "#606040" }}>
                                Origen:
                              </strong>{" "}
                              Acta #{compromiso.fromActaNumber}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "#0369a1", fontStyle: "italic" }}
                  >
                    * Los compromisos marcados como "En curso" se agregarán
                    automáticamente al acta actual.
                    <br />* Los compromisos marcados como "Terminado" se
                    considerarán completados y no se incluirán en el acta
                    actual.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "#f8f8f8",
                    borderRadius: 1,
                    mb: 3,
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    No se encontraron compromisos pendientes del acta anterior
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    Esto puede deberse a que no existe un acta previa con la
                    misma combinación de contratos y proyectos, o a que todos
                    los compromisos ya fueron completados.
                  </Typography>
                </Box>
              )}

              {/* Compromisos actuales */}
              {formData.compromisos.map((compromiso, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #d1d5db",
                    borderRadius: 1,
                    position: "relative",
                  }}
                >
                  {editingCompromisoIndex === index ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pr: 6,
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Compromiso"
                        name="compromiso"
                        value={tempCompromiso.compromiso}
                        onChange={handleTempCompromisoChange}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                      />
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Responsable"
                          name="responsable"
                          value={tempCompromiso.responsable}
                          onChange={handleTempCompromisoChange}
                          variant="outlined"
                          size="small"
                        />
                        <DatePicker
                          label="Fecha de cumplimiento"
                          value={tempCompromiso.fechaCumplimiento}
                          onChange={(newValue) =>
                            setTempCompromiso((prev) => ({
                              ...prev,
                              fechaCumplimiento: newValue,
                            }))
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              variant: "outlined",
                            },
                          }}
                          format="dd/MM/yyyy"
                        />
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Typography
                        sx={{ color: "#121212", fontWeight: "bold" }}
                        variant="subtitle1"
                      >
                        {compromiso.compromiso}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                        <Typography variant="body2">
                          <strong style={{ color: "#777" }}>
                            Responsable:
                          </strong>{" "}
                          {compromiso.responsable}
                        </Typography>
                        <Typography variant="body2">
                          <strong style={{ color: "#777" }}>Fecha:</strong>{" "}
                          {compromiso.fechaCumplimiento?.toLocaleDateString()}
                        </Typography>
                      </Box>
                    </>
                  )}
                  <Box sx={{ position: "absolute", right: 10, top: 10 }}>
                    {editingCompromisoIndex === index ? (
                      <>
                        <IconButton
                          size="small"
                          sx={{ color: "#4caf50" }}
                          onClick={handleGuardarCompromiso}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: "#f44336" }}
                          onClick={handleCancelarCompromiso}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          sx={{ color: "#696cff" }}
                          onClick={() => handleEditarCompromiso(index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: "tomato" }}
                          onClick={() => handleEliminarCompromiso(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              ))}

              {/* Formulario para agregar nuevo compromiso */}
              <Box sx={{ mt: 3, borderRadius: 1 }}>
                <Typography
                  sx={{ color: "#2b2b2b", fontWeight: "600" }}
                  variant="subtitle2"
                  gutterBottom
                >
                  Nuevo compromiso
                </Typography>
                <Grid container spacing={2}>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      gap: 1,
                      ml: 2,
                    }}
                  >
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        label="Compromiso"
                        name="compromiso"
                        value={nuevoCompromiso.compromiso}
                        onChange={handleCompromisoChange}
                        variant="outlined"
                        size="small"
                        placeholder="Descripción del compromiso"
                      />
                    </Grid>
                    <Grid item md={6}>
                      <TextField
                        fullWidth
                        label="Responsable"
                        name="responsable"
                        value={nuevoCompromiso.responsable}
                        onChange={handleCompromisoChange}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item width="40%">
                      <DatePicker
                        label="Fecha de cumplimiento"
                        value={nuevoCompromiso.fechaCumplimiento}
                        onChange={(newValue) =>
                          setNuevoCompromiso((prev) => ({
                            ...prev,
                            fechaCumplimiento: newValue,
                          }))
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            variant: "outlined",
                            sx: {
                              "& .MuiInputBase-root": {
                                height: "42px",
                              },
                              width: "100%",
                              "& .MuiInputLabel-root": {
                                transform: "translate(14px, 9px)",
                                "&.MuiInputLabel-shrink": {
                                  transform:
                                    "translate(14px, -9px) scale(0.75)",
                                },
                              },
                            },
                          },
                        }}
                        format="dd/MM/yyyy"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAgregarCompromiso}
                        fullWidth
                        sx={{ bgcolor: "#6164ff", p: 1 }}
                      >
                        + Agregar
                      </Button>
                    </Grid>
                  </Box>
                </Grid>
              </Box>
            </Box>

            {/* Sección: Asistentes */}
            <Box
              sx={{ mb: 4, border: "1px solid #d1d5db", p: 2, borderRadius: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: ".4rem" }}>
                <InfoIconWithTooltip
                  y="-25"
                  title="Registrar nombres, cargo, dependencia, tipo de asistencia (presencial o virtual) y firma de los participantes."
                />
                <Typography variant="h6" gutterBottom>
                  10. Asistentes
                </Typography>
              </Box>

              {formData.asistentes.length > 0 ? (
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>No.</TableCell>
                        <TableCell>Nombres y Apellidos</TableCell>
                        <TableCell>Cargo o rol - Dependencia</TableCell>
                        <TableCell>Institución/Organización</TableCell>
                        <TableCell>Correo Electrónico</TableCell>
                        <TableCell>Asistió</TableCell>
                        {!formData.sinFirma && <TableCell>Firma</TableCell>}
                        <TableCell>Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.asistentes.map((asistente, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {editingAsistenteIndex === index ? (
                              <TextField
                                fullWidth
                                label="Nombres y Apellidos"
                                name="nombre"
                                value={tempAsistente.nombre}
                                onChange={handleTempAsistenteChange}
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              asistente.nombre
                            )}
                          </TableCell>
                          <TableCell>
                            {editingAsistenteIndex === index ? (
                              <TextField
                                fullWidth
                                label="Cargo o rol - Dependencia"
                                name="cargo"
                                value={tempAsistente.cargo}
                                onChange={handleTempAsistenteChange}
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              asistente.cargo
                            )}
                          </TableCell>
                          <TableCell>
                            {editingAsistenteIndex === index ? (
                              <TextField
                                fullWidth
                                label="Institución/Organización"
                                name="institucion"
                                value={tempAsistente.institucion}
                                onChange={handleTempAsistenteChange}
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              asistente.institucion
                            )}
                          </TableCell>
                          <TableCell>
                            {editingAsistenteIndex === index ? (
                              <TextField
                                fullWidth
                                label="Correo Electrónico"
                                name="email"
                                type="email"
                                value={tempAsistente.email}
                                onChange={handleTempAsistenteChange}
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              asistente.email
                            )}
                          </TableCell>
                          <TableCell>
                            {editingAsistenteIndex === index ? (
                              <FormControl
                                fullWidth
                                variant="outlined"
                                size="small"
                              >
                                <InputLabel>Asistió</InputLabel>
                                <Select
                                  name="asistio"
                                  value={tempAsistente.asistio}
                                  onChange={handleTempAsistenteChange}
                                  label="Asistió"
                                >
                                  <MenuItem value="Presencial">
                                    Presencial
                                  </MenuItem>
                                  <MenuItem value="Virtual">Virtual</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Chip
                                label={asistente.asistio}
                                size="small"
                                color={
                                  asistente.asistio === "Presencial"
                                    ? "success"
                                    : "primary"
                                }
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          {!formData.sinFirma && (
                            <TableCell>
                              {asistente.firma ? (
                                <Chip
                                  label="Firmado"
                                  size="small"
                                  color="success"
                                  variant="filled"
                                />
                              ) : (
                                <Chip
                                  label="Sin firmar"
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {editingAsistenteIndex === index ? (
                              <>
                                <IconButton
                                  size="small"
                                  sx={{ color: "#4caf50" }}
                                  onClick={handleGuardarAsistente}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  sx={{ color: "#f44336" }}
                                  onClick={handleCancelarAsistente}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton
                                  size="small"
                                  sx={{ color: "#696cff" }}
                                  onClick={() => handleEditarAsistente(index)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  sx={{ color: "tomato" }}
                                  onClick={() => handleEliminarAsistente(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "#f8f8f8",
                    borderRadius: 1,
                    mb: 3,
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    Aún no se han registrado asistentes para esta reunión
                  </Typography>
                </Box>
              )}

              {/* Formulario para agregar asistente */}
              <Box
                sx={{
                  mt: 3,
                  border: "1px solid #eee",
                  p: 2,
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "rgba(105, 108, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(105, 108, 255, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#696cff",
                    },
                  },
                }}
              >
                <Typography
                  sx={{ color: "#2b2b2b", fontWeight: "600" }}
                  variant="subtitle2"
                  gutterBottom
                >
                  Agregar asistente
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={2.4}>
                    <TextField
                      fullWidth
                      label="Nombres y Apellidos"
                      name="nombre"
                      value={nuevoAsistente.nombre}
                      onChange={handleAsistenteChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <TextField
                      fullWidth
                      label="Cargo o rol - Dependencia"
                      name="cargo"
                      value={nuevoAsistente.cargo}
                      onChange={handleAsistenteChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <TextField
                      fullWidth
                      label="Institución/Organización"
                      name="institucion"
                      value={nuevoAsistente.institucion}
                      onChange={handleAsistenteChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <TextField
                      fullWidth
                      label="Correo Electrónico"
                      name="email"
                      type="email"
                      value={nuevoAsistente.email}
                      onChange={handleAsistenteChange}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Asistió</InputLabel>
                      <Select
                        name="asistio"
                        value={nuevoAsistente.asistio}
                        onChange={handleAsistenteChange}
                        label="Asistió"
                      >
                        <MenuItem value="Presencial">Presencial</MenuItem>
                        <MenuItem value="Virtual">Virtual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "right" }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAgregarAsistente}
                      fullWidth
                      sx={{ bgcolor: "#6164ff", width: "17%", p: 1 }}
                    >
                      + Agregar
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>

          {/* Botones de acción */}
          <Box
            sx={{
              p: 3,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{ bgcolor: "#6164ff" }}
            >
              {isSubmitting ? (editingActa ? "Actualizando..." : "Enviando...") : (editingActa ? "Actualizar acta" : "Enviar para aprobación")}
            </Button>
            {(!editingActa || (editingActa && editingActa.approvalStatus === 'borrador')) && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSaveDraft}
                disabled={isSubmitting}
                startIcon={<SaveIcon />}
                sx={{ color: "#6164ff", borderColor: "#6164ff" }}
              >
                Guardar borrador
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={handlePreview}
              sx={{ color: "#6164ff", borderColor: "#6164ff" }}
            >
              Previsualizar
            </Button>
            <Button variant="outlined" color="inherit">
              Cancelar
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Modal de previsualización del PDF */}
      <Dialog
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#6164ff",
            color: "white",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6">Previsualización del Acta</Typography>
          <IconButton
            onClick={() => setShowPreviewModal(false)}
            sx={{ color: "white" }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            height: "calc(90vh - 130px)",
            minHeight: "500px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {showPreviewModal && (
            <PDFViewer
              width="100%"
              height="100%"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                flex: 1,
              }}
            >
              <ActaPDF data={getPreviewData()} />
            </PDFViewer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexShrink: 0 }}>
          <Button
            onClick={() => setShowPreviewModal(false)}
            variant="outlined"
            sx={{ color: "#6164ff", borderColor: "#6164ff" }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Subir Acta Definitiva */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#6164ff",
            color: "white",
          }}
        >
          <Typography variant="h6">Subir Acta Definitiva</Typography>
          <IconButton
            onClick={() => setShowUploadDialog(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 3, color: "#666" }}>
            Sube un PDF de acta externa. Esta acta se vinculará al
            proyecto/contrato seleccionado y aparecerá en el historial con
            funciones de descarga, eliminación y sincronización con OneDrive. El
            número de acta se genera automáticamente manteniendo el consecutivo
            del proyecto.
          </Typography>

          <Grid container spacing={3}>
            {/* Información General del Acta */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "#6164ff", fontWeight: "bold" }}
              >
                📋 Información General del Acta
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Acta *</InputLabel>
                <Select
                  value={uploadFormData.tipoActa || ""}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setUploadFormData((prev) => ({
                      ...prev,
                      tipoActa: newType,
                      tipoActaCustom: "",
                      comiteId: "",
                      proyecto: "",
                      producto: "",
                      numeroActa: "",
                    }));
                    setUploadActaNumberError("");
                  }}
                  label="Tipo de Acta *"
                >
                  <MenuItem value="REUNION">
                    Reunión del contrato o convenio
                  </MenuItem>
                  <MenuItem value="COMITE">Comité</MenuItem>
                  <MenuItem value="OTRO">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Campo personalizado para tipo de acta cuando se selecciona OTRO */}
            {uploadFormData.tipoActa === "OTRO" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoría Otro *</InputLabel>
                  <Select
                    value={uploadFormData.tipoActaCustom || ""}
                    onChange={(e) => {
                      const selectedCat = e.target.value;
                      setUploadFormData((prev) => ({
                        ...prev,
                        tipoActaCustom: selectedCat,
                      }));
                      if (selectedCat) {
                        fetchNextActaNumberForCategoryOther(selectedCat, true);
                      }
                    }}
                    label="Categoría Otro *"
                    disabled={loadingCategoriasOtro}
                  >
                    {loadingCategoriasOtro ? (
                      <MenuItem disabled>Cargando categorías...</MenuItem>
                    ) : categoriasOtro.length > 0 ? (
                      categoriasOtro.map((categoria) => (
                        <MenuItem key={categoria.id} value={categoria.nombre}>
                          {categoria.nombre}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No hay categorías disponibles</MenuItem>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      value="__NUEVO__"
                      sx={{
                        fontStyle: "italic",
                        color: "#1976d2",
                        fontWeight: 600,
                      }}
                    >
                      ➕ Otra categoría (Ingresar manualmente)...
                    </MenuItem>
                  </Select>
                </FormControl>
                {uploadFormData.tipoActaCustom === "__NUEVO__" && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Nombre de la nueva categoría *"
                    value={uploadNuevaCategoriaOtroNombre}
                    onChange={(e) => {
                      setUploadNuevaCategoriaOtroNombre(e.target.value);
                      setUploadFormData((prev) => ({ ...prev, numeroActa: "1" }));
                    }}
                    placeholder="Ej: Taller de socialización"
                    sx={{ mt: 1.5 }}
                    helperText="Se guardará automáticamente al subir el acta y quedará disponible para futuras actas"
                  />
                )}
              </Grid>
            )}

            {/* Selector de Comité cuando se selecciona COMITE */}
            {uploadFormData.tipoActa === "COMITE" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Comité *</InputLabel>
                  <Select
                    value={uploadFormData.comiteId || ""}
                    onChange={(e) => {
                      const selectedComiteId = e.target.value;
                      setUploadFormData((prev) => ({
                        ...prev,
                        comiteId: selectedComiteId,
                      }));
                      if (selectedComiteId) {
                        fetchNextActaNumberForCommittee(selectedComiteId, true);
                      }
                    }}
                    label="Comité *"
                    disabled={loadingComites}
                  >
                    {loadingComites ? (
                      <MenuItem disabled>Cargando comités...</MenuItem>
                    ) : comites.length > 0 ? (
                      comites.map((comite) => (
                        <MenuItem key={comite.id} value={comite.id}>
                          {comite.nombre}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No hay comités disponibles</MenuItem>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      value="__NUEVO__"
                      sx={{
                        fontStyle: "italic",
                        color: "#1976d2",
                        fontWeight: 600,
                      }}
                    >
                      ➕ Otro (Ingresar nuevo comité manualmente)...
                    </MenuItem>
                  </Select>
                </FormControl>
                {uploadFormData.comiteId === "__NUEVO__" && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Nombre del nuevo comité *"
                    value={uploadNuevoComiteNombre}
                    onChange={(e) => {
                      setUploadNuevoComiteNombre(e.target.value);
                      setUploadFormData((prev) => ({ ...prev, numeroActa: "1" }));
                    }}
                    placeholder="Ej: Comité de Ética e Investigación"
                    sx={{ mt: 1.5 }}
                    helperText="Se guardará automáticamente al subir el acta y quedará disponible para futuras actas"
                  />
                )}
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={es}
              >
                <DatePicker
                  label="Fecha del Acta *"
                  value={uploadFormData.fecha}
                  onChange={(newValue) =>
                    setUploadFormData((prev) => ({ ...prev, fecha: newValue }))
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            {/* Separador visual e Información del Proyecto/Contrato solo cuando NO es COMITE y NO es OTRO */}
            {uploadFormData.tipoActa !== "COMITE" && uploadFormData.tipoActa !== "OTRO" && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "#6164ff", fontWeight: "bold" }}
                  >
                    🏢 Información del Proyecto/Contrato
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Contrato *</InputLabel>
                    <Select
                      value={uploadFormData.proyecto}
                      onChange={async (e) => {
                        const selectedProjectId = e.target.value;

                        setUploadFormData((prev) => ({
                          ...prev,
                          proyecto: selectedProjectId,
                          producto: "", // Reset product when project changes
                        }));

                        // Calculate next acta number for this project
                        if (selectedProjectId) {
                          // Para actas externas, usar la función normal sin estado específico
                          updateUploadActaNumber(selectedProjectId);
                        }
                      }}
                      label="Contrato *"
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      Proyecto
                      {uploadFormData.tipoActa === "REUNION" ? (
                        <span style={{ color: "red" }}> *</span>
                      ) : (
                        " *"
                      )}
                    </InputLabel>
                    <Select
                      value={uploadFormData.producto}
                      onChange={async (e) => {
                        const selectedProduct = e.target.value;
                        setUploadFormData((prev) => ({
                          ...prev,
                          producto: selectedProduct,
                        }));

                        // Calculate next acta number
                        if (uploadFormData.proyecto) {
                          // Para actas externas, usar la función normal sin estado específico
                          updateUploadActaNumber(
                            uploadFormData.proyecto,
                            selectedProduct || null
                          );
                        }
                      }}
                      label={`Proyecto${
                        uploadFormData.tipoActa === "REUNION" ? " *" : " *"
                      }`}
                      disabled={!uploadFormData.proyecto}
                    >
                      <MenuItem value="">Seleccione un proyecto</MenuItem>
                      <MenuItem value="TRANSVERSAL">TRANSVERSAL</MenuItem>
                      {uploadFormData.proyecto &&
                        projects
                          .find((p) => p.id === uploadFormData.proyecto)
                          ?.products?.map((product, index) => (
                            <MenuItem key={index} value={product}>
                              {product}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Separador visual */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "#6164ff", fontWeight: "bold" }}
              >
                📊 Detalles del Acta
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Número de Acta *"
                value={uploadFormData.numeroActa}
                onChange={async (e) => {
                  const newNumber = e.target.value;
                  setUploadFormData((prev) => ({
                    ...prev,
                    numeroActa: newNumber,
                  }));

                  // Validate acta number if we have project and number
                  if (newNumber && uploadFormData.proyecto) {
                    const validation = await validateActaNumber(
                      newNumber,
                      uploadFormData.proyecto,
                      uploadFormData.producto || null
                    );

                    // Para actas externas no aplicamos restricciones de estado
                    if (!validation.isValid) {
                      setUploadActaNumberError(validation.message);
                    } else {
                      setUploadActaNumberError("");
                    }
                  } else {
                    setUploadActaNumberError("");
                  }
                }}
                placeholder="Ej: 1, 2, 3..."
                helperText={uploadActaNumberError || "Consecutivo automático"}
                error={!!uploadActaNumberError}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: "#6164ff", fontWeight: "bold" }}>
                      #
                    </Box>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Responsable *"
                value={uploadFormData.responsable}
                onChange={(e) =>
                  setUploadFormData((prev) => ({
                    ...prev,
                    responsable: e.target.value,
                  }))
                }
                placeholder="Nombre del responsable de la reunión"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: "#6164ff" }}>👤</Box>
                  ),
                }}
              />
            </Grid>

            {/* Campos ocultos: lugar de la reunión y objetivo de la reunión */}
            {/* 
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lugar de la reunión"
                value={uploadFormData.lugar || ''}
                onChange={(e) => setUploadFormData(prev => ({ ...prev, lugar: e.target.value }))}
                placeholder="Ej: Presencial, Virtual, Híbrido"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: '#6164ff' }}>
                      📍
                    </Box>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Objetivo de la reunión *"
                value={uploadFormData.objetivo}
                onChange={(e) => setUploadFormData(prev => ({ ...prev, objetivo: e.target.value }))}
                placeholder="Describa el propósito principal de la reunión"
                multiline
                rows={3}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: '#6164ff', alignSelf: 'flex-start', mt: 1 }}>
                      🎯
                    </Box>
                  )
                }}
              />
            </Grid>
            */}

            {/* Separador visual */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "#6164ff", fontWeight: "bold" }}
              >
                📄 Archivo PDF
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: "2px dashed #6164ff",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  bgcolor: uploadFormData.pdfFile ? "#f8f9ff" : "#fafafa",
                  transition: "all 0.3s ease",
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    color: "#6164ff",
                    borderColor: "#6164ff",
                    mb: 1,
                  }}
                >
                  {uploadFormData.pdfFile
                    ? "Cambiar archivo"
                    : "Seleccionar archivo PDF *"}
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) =>
                      setUploadFormData((prev) => ({
                        ...prev,
                        pdfFile: e.target.files[0],
                      }))
                    }
                    hidden
                  />
                </Button>
                {uploadFormData.pdfFile && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#6164ff", fontWeight: "bold" }}
                    >
                      📄 {uploadFormData.pdfFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Tamaño:{" "}
                      {(uploadFormData.pdfFile.size / 1024 / 1024).toFixed(2)}{" "}
                      MB
                    </Typography>
                  </Box>
                )}
                {!uploadFormData.pdfFile && (
                  <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                    Seleccione un archivo PDF del acta definitiva
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "#f8f9ff" }}>
          <Button
            onClick={() => setShowUploadDialog(false)}
            variant="outlined"
            sx={{ color: "#6164ff", borderColor: "#6164ff" }}
            startIcon={<CancelIcon />}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUploadExternalActa}
            variant="contained"
            disabled={
              uploadLoading ||
              !uploadFormData.tipoActa ||
              (uploadFormData.tipoActa === "OTRO" &&
                (!uploadFormData.tipoActaCustom ||
                  (uploadFormData.tipoActaCustom === "__NUEVO__" &&
                    !uploadNuevaCategoriaOtroNombre.trim()))) ||
              (uploadFormData.tipoActa === "COMITE" &&
                (!uploadFormData.comiteId ||
                  (uploadFormData.comiteId === "__NUEVO__" &&
                    !uploadNuevoComiteNombre.trim()))) ||
              (uploadFormData.tipoActa !== "COMITE" &&
                uploadFormData.tipoActa !== "OTRO" &&
                !uploadFormData.proyecto) ||
              (uploadFormData.tipoActa === "REUNION" &&
                !uploadFormData.producto) ||
              !uploadFormData.numeroActa ||
              !uploadFormData.pdfFile ||
              !uploadFormData.responsable ||
              !!uploadActaNumberError
            }
            startIcon={
              uploadLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
            sx={{
              bgcolor: "#6164ff",
              "&:hover": { bgcolor: "#5558dd" },
            }}
          >
            {uploadLoading ? "Subiendo..." : "Subir Acta Definitiva"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de encargados eliminado - se asignan al momento de aprobar */}

      {/* Dialog para confirmar carga de datos desde caché */}
      <Dialog
        open={showCacheDialog}
        onClose={() => setShowCacheDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f8f9ff",
            color: "#6164ff",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          💾 Datos Guardados Encontrados
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Se encontraron datos guardados de un formulario anterior. ¿Qué desea
            hacer?
          </Typography>
          <Box
            sx={{
              bgcolor: "#f8f9ff",
              p: 2,
              borderRadius: 2,
              border: "1px solid #e0e4ff",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              💡 <strong>Continuar:</strong> Carga los datos guardados para
              continuar donde lo dejó
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              🆕 <strong>Nuevo formulario:</strong> Elimina los datos guardados
              y comienza desde cero
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleStartNew}
            variant="outlined"
            sx={{
              color: "#666",
              borderColor: "#ddd",
              "&:hover": {
                borderColor: "#999",
                bgcolor: "#f5f5f5",
              },
            }}
            startIcon={<ClearIcon />}
          >
            Nuevo Formulario
          </Button>
          <Button
            onClick={handleContinueWithCache}
            variant="contained"
            sx={{
              bgcolor: "#6164ff",
              "&:hover": { bgcolor: "#5558dd" },
            }}
            startIcon={<CheckIcon />}
          >
            Continuar con Datos Guardados
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Parámetros de IA, MiniMax y métricas de tokens (solo Admin) */}
      {isAdmin && (
        <AiParametersDialog
          open={aiParametersOpen}
          onClose={() => setAiParametersOpen(false)}
          onConfigSaved={(updatedConfig) => setCurrentAiConfig(updatedConfig)}
        />
      )}
    </LocalizationProvider>
  );
};

export default ActaForm;
